import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import Layout, { PageHeader } from "../components/Layout";
import { api, saleRefundRows, money, formatApiError, fmtDateTime, STATUS_LABELS, STATUS_COLORS, STATUS_ORDER } from "../lib/api";
import { userCan } from "../lib/permissions";
import { toast } from "sonner";
import { ArrowLeft, Printer, ChatCircle, Trash, Receipt as ReceiptIcon, ChatCircleText, EnvelopeSimple, PencilSimple, MagnifyingGlass, X, Plus, CaretDown, CreditCard, CurrencyDollar, PaperPlaneTilt, Globe, CopySimple, CheckCircle, Clock, ArrowSquareOut, Warning, ArrowCounterClockwise, Eye } from "@phosphor-icons/react";
import { PROBLEMS, MANUFACTURERS, MODELS, DEVICE_TYPES } from "../data/repairCatalog";
import LabelPrint from "../components/LabelPrint";
import { useAuth } from "../context/AuthContext";
import PinPromptModal from "../components/PinPromptModal";
import SendTicketSmsModal from "../components/SendTicketSmsModal";
import SendTicketEmailModal from "../components/SendTicketEmailModal";
import { OI_STATUS_COLORS, OI_STATUS_LABELS, copyInvoiceLink, fmtInvoiceDate } from "../components/OnlineInvoiceDetailModal";
import UnifiedInvoiceModal, { AddPaymentModal } from "../components/UnifiedInvoiceModal";
import RefundModal from "../components/RefundModal";
import CatalogItemPicker from "../components/CatalogItemPicker";
import PaymentSummary from "../components/PaymentSummary";
import { DeviceIdentity, parseDeviceInfo } from "../components/DeviceIdentity";
import { HistoryPanel } from "../components/HistoryPanel";
import { NoteComposer } from "../components/NoteItem";
import { TicketNotesThread } from "../components/TicketNotesThread";
import { ReceiptFooter, isFullyRefunded } from "../components/ReceiptFooter";
import { TicketHeader } from "../components/TicketHeader";
import { TicketAssignCard } from "../components/TicketAssignCard";
import { ticketHref } from "../components/InboxThread";
import { CustomerBadges } from "../components/CustomerBadges";

/** Strip brand prefix from model so the title is just the model name */
function modelOnly(make, model) {
  let m = (model || "").trim();
  const brand = (make || "").trim();
  if (brand && m.toLowerCase().startsWith(brand.toLowerCase() + " ")) {
    m = m.slice(brand.length).trim();
  }
  return m || "Unknown Device";
}

function modelSlug(value) {
  return String(value || "")
    .replace(/^web_/i, "")
    .split("·")[0]
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function deviceModelKey(make, model) {
  return modelSlug(modelOnly(make, model));
}

function modelFromStockKey(stockKey) {
  // Only website stock keys encode a trustworthy model prefix:
  // `web_surface-pro-7::screen-repair`. Local custom repair IDs use the
  // form `cat_<uuid>` and are NOT devices — treating one as a model created
  // the phantom “Device #2” seen on existing tickets.
  const rawKey = String(stockKey || "").trim();
  if (!/^web_/i.test(rawKey)) return "";
  const raw = rawKey.replace(/^web_/i, "").trim();
  if (!raw || !raw.includes("::")) return "";
  return raw.split("::")[0].trim().toLowerCase();
}

function labelFromModelSlug(slug) {
  const branded = {
    iphone: "iPhone", ipad: "iPad", macbook: "MacBook", imac: "iMac",
    airpods: "AirPods", apple: "Apple", se: "SE", pro: "Pro", max: "Max",
    mini: "mini", ultra: "Ultra", plus: "Plus",
  };
  return String(slug || "")
    .split("-")
    .filter(Boolean)
    .map((word) => branded[word] || (word.match(/^\d+$/) ? word : `${word[0]?.toUpperCase() || ""}${word.slice(1)}`))
    .join(" ");
}

function makeForModelSlug(slug) {
  return /^(iphone|ipad|macbook|imac|apple-watch|airpods)/.test(slug || "") ? "Apple" : "";
}

function hasDeviceIdentity(device) {
  return Boolean(device?.device_make || device?.device_model || device?.imei || device?.issue);
}

/**
 * Older ticket edits can retain every repair line on the primary ticket even
 * when its catalog stock key belongs to another model. Group by that stable
 * model key so the service history remains truthful without rewriting audit
 * data or stock movements.
 */
function ticketDeviceGroups(ticket) {
  // Each recorded device is its own group (a school can bring three identical
  // iPads); only catalog-inferred models merge by model key.
  const groups = [];
  const addDevice = (device, source = "record", ref = null) => {
    if (!hasDeviceIdentity(device)) return null;
    const modelKey = deviceModelKey(device.device_make, device.device_model) || `device-${groups.length + 1}`;
    const sameModel = groups.filter((group) => group.modelKey === modelKey);
    const bareDuplicate = source === "record" && sameModel.length > 0 && !device.issue && !device.imei;
    if ((source === "inferred" || bareDuplicate) && sameModel.length > 0) return sameModel[0];
    const group = {
      key: sameModel.length ? `${modelKey}#${sameModel.length + 1}` : modelKey,
      modelKey,
      ref,
      device,
      source,
      make: device.device_make || makeForModelSlug(modelKey),
      type: device.device_type || "",
      model: device.device_model || labelFromModelSlug(modelKey) || "Unknown device",
      year: device.device_year || "",
      modelNumber: device.device_model_number || "",
      imei: device.imei || "",
      repairs: [],
    };
    groups.push(group);
    return group;
  };

  const primary = addDevice(ticket || {}, "record", "primary");
  (ticket?.additional_devices || []).forEach((device, i) => addDevice(device, "record", device._key || `dev-${i}`));

  const groupForRepair = (line) => {
    const repairModel = modelFromStockKey(line?.stock_key);
    if (repairModel) {
      const matching = groups.find((group) => group.modelKey === repairModel);
      if (matching) return matching;
      return addDevice({
        device_make: makeForModelSlug(repairModel),
        device_model: labelFromModelSlug(repairModel),
      }, "inferred");
    }
    return primary || groups[0] || addDevice({ device_model: "Unknown device" }, "inferred");
  };

  let repairLines = Array.isArray(ticket?.repair_lines) && ticket.repair_lines.length > 0
    ? ticket.repair_lines
    : parseIssueToRepairs(ticket?.issue);
  if (!repairLines.length && (ticket?.issue || "").trim() && !(ticket?.additional_devices || []).length) {
    repairLines = [{ name: ticket.issue.split("\n")[0].trim(), price: Number(ticket.estimated_cost) || 0 }];
  }
  repairLines.forEach((line) => {
    const group = groupForRepair(line);
    if (group) group.repairs.push({ name: line.name, price: Number(line.price) || 0, stock_key: line.stock_key || null });
  });

  groups.filter((group) => group.source === "record" && group.ref !== "primary" && group.repairs.length === 0)
    .forEach((group) => parseIssueToRepairs(group.device.issue).forEach((repair) => group.repairs.push(repair)));

  // One recorded device whose repairs all carry a differently-named catalog
  // model (e.g. 'MacBook Pro 13" (M1)' vs 'macbook-pro-13-m1-a2338') is the
  // SAME device — never show it as a second, empty one.
  const inferred = groups.filter((group) => group.source === "inferred");
  const recorded = groups.filter((group) => group.source !== "inferred");
  if (primary && recorded.length === 1 && primary.repairs.length === 0 && inferred.length === 1) {
    primary.repairs.push(...inferred[0].repairs);
    primary.aliasKeys = [inferred[0].key];
    return [primary];
  }

  return groups;
}

function ticketDeviceTitle(groups) {
  const counts = new Map();
  groups.map((group) => modelOnly(group.make, group.model)).filter(Boolean)
    .forEach((name) => counts.set(name, (counts.get(name) || 0) + 1));
  const names = [...counts].map(([name, n]) => (n > 1 ? `${n}× ${name}` : name));
  if (names.length === 0) return "Repair Ticket";
  if (names.length <= 2) return names.join(" + ");
  return `${names.slice(0, 2).join(" + ")} + ${names.length - 2} more`;
}

function deviceLabelForRepair(line, groups) {
  const repairModel = modelFromStockKey(line?.stock_key);
  const match = repairModel && groups.find((group) => group.modelKey === repairModel);
  const fallback = groups[0];
  return match || fallback ? modelOnly((match || fallback).make, (match || fallback).model) : "Unassigned device";
}

export default function RepairTicketDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [t, setT] = useState(null);
  const [note, setNote] = useState("");
  const [noteAction, setNoteAction] = useState(null);
  const [staff, setStaff] = useState([]);
  useEffect(() => {
    api.get(`/repair-tickets/${id}/mentionable`).then((r) => setStaff(r.data)).catch(() => setStaff([]));
  }, [id]);

  const [pinOpen, setPinOpen] = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [sendInvoiceOpen, setSendInvoiceOpen] = useState(false);
  const [store, setStore] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  // Sibling tickets — other devices intaken in the same batch
  const [siblings, setSiblings] = useState([]);
  // All sales (active + voided) that reference this ticket. Used to surface
  // the invoice number in the ticket header AND warn cashiers when a
  // duplicate sale has been rung up against the same repair (real prod bug
  // we saw on 2026-05-22: ticket got billed twice = $1050 x 2 = $2100).
  const [linkedSales, setLinkedSales] = useState({ sales: [], active_count: 0, voided_count: 0, is_duplicate: false });
  const [onlineInvoices, setOnlineInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [addPaymentInvoice, setAddPaymentInvoice] = useState(null);
  const [bannerRefundInvoice, setBannerRefundInvoice] = useState(null);

  const load = () => api.get(`/repair-tickets/${id}`).then((r) => setT(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Pull linked sales (one extra request, cached on backend by id).
  useEffect(() => {
    if (!id) return;
    api.get(`/repair-tickets/${id}/linked-sales`)
      .then((r) => setLinkedSales(r.data || { sales: [], active_count: 0, voided_count: 0, is_duplicate: false }))
      .catch(() => {});
  }, [id]);

  // Pull linked online invoices
  const loadOnlineInvoices = useCallback(() => {
    if (!id) return;
    api.get(`/online-invoices/by-ticket/${id}`)
      .then((r) => setOnlineInvoices(r.data?.items || []))
      .catch(() => setOnlineInvoices([]));
  }, [id]);
  useEffect(() => { loadOnlineInvoices(); }, [loadOnlineInvoices]);

  // Pull sibling tickets. Backend returns [] for non-batch tickets, so this
  // is a cheap no-op on the common single-device case.
  useEffect(() => {
    if (!id) return;
    api.get(`/repair-tickets/${id}/siblings`)
      .then((r) => setSiblings(Array.isArray(r.data) ? r.data : []))
      .catch(() => setSiblings([]));
  }, [id]);

  // Load store (for SMS template variables — {store} name, {store_phone})
  useEffect(() => {
    if (!t?.store_id) return;
    api.get("/stores").then((r) => {
      const match = r.data.find((s) => s.id === t.store_id);
      if (match) setStore(match);
    }).catch(() => {});
  }, [t?.store_id]);

  const updateField = async (patch) => {
    try {
      const { data } = await api.patch(`/repair-tickets/${id}`, patch);
      setT(data);
      toast.success("Updated");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addNote = async (mentions = []) => {
    if (!note.trim()) return;
    try {
      const { data } = await api.post(`/repair-tickets/${id}/notes`, { text: note, mentions });
      setT(data);
      setNote("");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const confirmNoteAction = async (pin) => {
    const header = user?.role === "admin" ? "X-Admin-Pin" : "X-Staff-Pin";
    const url = `/repair-tickets/${id}/notes/${noteAction.note.id}`;
    const { data } = noteAction.type === "edit"
      ? await api.patch(url, { text: noteAction.text, mentions: noteAction.mentions || [] }, { headers: { [header]: pin } })
      : await api.delete(url, { headers: { [header]: pin } });
    setT(data);
    toast.success(noteAction.type === "edit" ? "Note updated" : "Note deleted");
    setNoteAction(null);
  };

  const del = () => setPinOpen(true);

  const confirmDelete = async (pin) => {
    // Staff confirm with their own 4-digit PIN (X-Staff-Pin); admins keep
    // using X-Admin-Pin. Either header is accepted by the backend's
    // verify_staff_or_admin_pin dependency.
    const header = user?.role === "admin" ? "X-Admin-Pin" : "X-Staff-Pin";
    await api.delete(`/repair-tickets/${id}`, { headers: { [header]: pin } });
    toast.success("Ticket deleted");
    nav("/repairs");
  };

  // Fetch the Dymo label PDF from the backend and open it in a new tab —
  // same pattern as printThermal. Works with any printer (label, office,
  // mobile AirPrint) without PrintNode being configured.
  const printLabel = async () => {
    try {
      const { data } = await api.get(`/repair-tickets/${id}/label.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const w = window.open(url, "_blank");
      if (!w) {
        const a = document.createElement("a");
        a.href = url;
        a.download = `label-${t.ticket_number}.pdf`;
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(formatApiError(e) || "Couldn't generate label"); }
  };

  // Fetch the 80mm thermal ticket-slip PDF from the backend and open it in a
  // new tab. Browsers show their native print dialog — works with any printer
  // (thermal or office) without PrintNode being configured.
  const printThermal = async () => {
    try {
      const { data } = await api.get(`/repair-tickets/${id}/slip.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const w = window.open(url, "_blank");
      // Some browsers block window.open unless triggered in a click handler —
      // this IS a click handler, but iOS Safari occasionally misbehaves, so
      // fall back to a download if nothing opens.
      if (!w) {
        const a = document.createElement("a");
        a.href = url;
        a.download = `ticket-${t.ticket_number}.pdf`;
        a.click();
      }
      // Revoke the blob URL a minute later — plenty of time for the tab to
      // load and the user to trigger print.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(formatApiError(e) || "Couldn't generate thermal slip"); }
  };

  // Open one specific POS sale in the shared invoice modal, so Void / Delete
  // (PIN-gated) act on that exact sale — used to clear a duplicate invoice.
  const openSaleInvoice = async (saleId) => {
    try {
      const { data: sale } = await api.get(`/sales/${saleId}`);
      const payments = (sale.payments || []).map((p, i) => ({
        payment_id: p.id || null,
        payment_index: i,
        method: p.method || "cash",
        amount: p.amount || 0,
        paid_at: p.paid_at || p.at || sale.created_at,
        cashier: p.cashier || p.cashier_name || sale.cashier_name || "Staff",
        is_refund: p.is_refund || false,
        note: p.note || "",
        tyro_meta: p.tyro_meta || null,
        corrected_at: p.corrected_at || null,
        correction_reason: p.correction_reason || "",
        tyro_offline: !!p.tyro_offline,
        tyro_auth_code: p.tyro_auth_code || "",
        edited_at: p.edited_at || null,
        edit_reason: p.edit_reason || "",
        edited_by_name: p.edited_by_name || "",
      }));
      const totalPaid = payments.reduce((a, p) => a + (p.is_refund || p.corrected_at ? 0 : p.amount), 0);
      const amountDue = Math.max(0, Number(sale.total || 0) - totalPaid);
      setSelectedInvoice({
        id: sale.id,
        invoice_number: sale.sale_number,
        customer_id: sale.customer_id || null,
        customer_name: sale.customer_name || t.customer_name || "Walk-in",
        customer_phone: sale.customer_phone || t.customer_phone || "",
        customer_email: sale.customer_email || "",
        total: sale.total,
        discount: sale.discount || 0,
        store_credit_redeemed: sale.store_credit_redeemed || 0,
        amount_due: amountDue,
        in_store_paid: totalPaid,
        status: sale.voided ? "voided" : amountDue <= 0.005 ? "paid" : "draft",
        items: (sale.items || []).map((it) => ({
          description: it.name || "Item",
          quantity: it.quantity || 1,
          unit_price: it.unit_price || 0,
          kind: it.kind || "product",
          repair_ticket_id: it.repair_ticket_id || null,
        })),
        payment_history: [...payments, ...saleRefundRows(sale)],
        devices: [],
        created_at: sale.created_at,
        linked_ticket_id: t.id,
        _is_sale: true,
      });
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't open that invoice");
    }
  };

  if (!t) return <Layout><div className="p-8 text-zinc-500">Loading…</div></Layout>;
  const visibleNotes = (t.notes || []).filter((n) => !/^(SMS|Email) →/.test(n.text || ""));

  const billing = ticketBilling(t, linkedSales, onlineInvoices);
  const deviceGroups = ticketDeviceGroups(t);
  const overpaidInv = onlineInvoices.find((oi) => oi.status !== "voided" && invoiceOverpaid(oi) > 0.005);
  const overpaidSaleAmt = Math.round((billing.totalPaid - billing.cost) * 100) / 100;
  const liveInv = onlineInvoices.find((oi) => oi.status !== "voided");
  const refundedNo = liveInv
    ? (isFullyRefunded(liveInv.total, Number(liveInv.refund_total || 0), liveInv.refund_closed) ? liveInv.invoice_number : null)
    : billing.settledSale && isFullyRefunded(billing.settledSale.total, Number(billing.settledSale.refund_total || 0)) ? billing.settledSale.sale_number : null;
  const overpaid = overpaidInv
    ? { amount: invoiceOverpaid(overpaidInv), review: () => setSelectedInvoice(overpaidInv) }
    : !onlineInvoices.length && billing.settledSale && overpaidSaleAmt > 0.005
      ? { amount: overpaidSaleAmt, review: () => openSaleInvoice(billing.settledSale.id) } : null;

  return (
    <Layout>
      <div className="bg-[#F8FAFC] min-h-full pb-10">
      <TicketHeader
        t={t}
        title={ticketDeviceTitle(deviceGroups)}
        devices={deviceGroups.map((d) => modelOnly(d.make, d.model))}
        billing={billing}
        onlineInvoices={onlineInvoices}
        overpaid={overpaid}
        refundedNo={refundedNo}
        canDelete={userCan(user, "ticket.delete")}
        actions={{ edit: () => setEditOpen(true), printThermal, printLabel, sms: () => setSmsOpen(true),
                   email: () => setEmailOpen(true), sendInvoice: () => setSendInvoiceOpen(true), del,
                   status: (s) => updateField({ status: s }) }}
      />

      <div className="px-4 sm:px-6 lg:px-8 pt-6 grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        <section className="lg:col-span-2 space-y-6">
          <PaymentMismatchBanner
            ticket={t}
            onlineInvoices={onlineInvoices}
            onOpenInvoice={(oi) => setSelectedInvoice(oi)}
            onRefund={(oi, amt) => setBannerRefundInvoice({ invoice: oi, prefillAmount: amt })}
            onSendLink={async (oi) => {
              try {
                await api.post(`/online-invoices/${oi.id}/send`, { via: "both" });
                toast.success("Payment link sent via SMS & email");
              } catch (e) {
                toast.error(formatApiError(e, "Failed to send payment link"));
              }
            }}
          />

          {billing.isDuplicate && (
            <div
              className="rounded-2xl border-2 border-red-300 bg-red-50 px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4"
              data-testid="duplicate-invoice-warning"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center shrink-0">
                  <Warning size={20} weight="fill" className="text-red-600" />
                </div>
                <div className="min-w-0">
                  <div className="text-[14px] font-bold text-red-800">
                    {billing.activeSales.length} invoices attached to this ticket
                  </div>
                  <div className="text-[12px] text-red-600 mt-0.5">
                    {billing.activeSales.map((s) => s.sale_number).join(" · ")} — the repair was billed more than once.
                    Void the one that shouldn't exist.
                  </div>
                </div>
              </div>
              {billing.activeSales
                .filter((s) => !(s.payments || []).some((p) => !p.corrected_at && Number(p.amount) > 0))
                .slice(0, 1)
                .map((s) => (
                  <button
                    key={s.id}
                    onClick={() => openSaleInvoice(s.id)}
                    className="shrink-0 h-10 px-5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-[12px] font-bold flex items-center gap-2 transition-colors"
                    data-testid="review-duplicate-invoice"
                  >
                    <ReceiptIcon size={14} weight="bold" /> Review {s.sale_number}
                  </button>
                ))}
            </div>
          )}

          {siblings.length > 0 && (
            <div
              className="rounded-xl border px-4 py-3 flex items-center gap-3 overflow-x-auto"
              style={{
                background: "linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)",
                borderColor: "#BFDBFE",
              }}
              data-testid="ticket-siblings"
            >
              <div className="shrink-0 text-[10px] tracking-[0.14em] uppercase font-bold text-[#1E40AF] pr-2 border-r border-blue-200">
                Batch intake · also for {t.customer_name?.split(" ")[0] || "this customer"}
              </div>
              {siblings.map((s) => (
                <Link
                  key={s.id}
                  to={`/repairs/${s.id}`}
                  data-testid={`sibling-${s.id}`}
                  className="shrink-0 inline-flex items-center gap-1.5 bg-white border border-blue-200 hover:border-[#1E5FEB] rounded-full pl-2.5 pr-3 py-1 transition-colors"
                >
                  <span className="text-[10px] font-mono font-bold text-[#1E5FEB]">{s.ticket_number}</span>
                  <span className="text-[11px] font-semibold text-slate-800 truncate max-w-[180px]">
                    {modelOnly(s.device_make, s.device_model)}
                  </span>
                </Link>
              ))}
            </div>
          )}

          <div className={CARD} data-testid="customer-card">
            <CustomerBadges t={t} />
            <div className="px-5 pb-5 grid grid-cols-2 sm:grid-cols-3 gap-4">
                <Info
                  label="Customer"
                  value={
                    t.customer_id ? (
                      <Link className="text-[#1E5FEB] hover:text-[#1849C8] hover:underline font-medium" to={`/customers/${t.customer_id}`} data-testid="ticket-customer-link">
                        {t.customer_name || "—"}
                      </Link>
                    ) : t.customer_phone ? (
                      <Link className="text-[#1E5FEB] hover:text-[#1849C8] hover:underline font-medium" to={`/customers?q=${encodeURIComponent(t.customer_phone)}`} data-testid="ticket-customer-link" title="No profile linked — search by phone">
                        {t.customer_name || "—"}
                      </Link>
                    ) : (
                      <span>{t.customer_name || "—"}</span>
                    )
                  }
                />
                <Info label="Phone" value={t.customer_phone || "—"} mono />
                <Info label="Passcode" value={t.passcode || "—"} mono />
            </div>
          </div>

          <div>
            <SectionLabel>Devices, repairs &amp; payment</SectionLabel>
            <TicketServiceCards ticket={t} renderTotal={(totalLabel) => (
              <>
              {onlineInvoices.length > 0 ? (
                <LinkedInvoiceSection
                  totalLabel={totalLabel}
                  invoices={onlineInvoices}
                  onOpenInvoice={(oi) => setSelectedInvoice(oi)}
                  onSendLink={async (oi) => {
                    await api.post(`/online-invoices/${oi.id}/send`, { via: "both" });
                    toast.success("Balance link sent via SMS & email");
                    load();
                  }}
                />
              ) : (
                <PaymentSummary
                  totalLabel={totalLabel}
                  ticket={t}
                  linkedSales={linkedSales}
                  onlineInvoices={onlineInvoices}
                  onOpenInvoice={(oi) => setSelectedInvoice(oi)}
                />
              )}

              </>
            )} />
          </div>

          <div className={`${CARD} p-5`} data-testid="notes-card">
            <SectionLabel icon={ChatCircle}>Notes timeline</SectionLabel>
            <TicketNotesThread ticket={t} notes={visibleNotes} user={user} staff={staff} onReload={load}
              onEditNote={(n, text, mentions) => setNoteAction({ type: "edit", note: n, text, mentions })}
              onDeleteNote={(n) => setNoteAction({ type: "delete", note: n })} />
            <div className="pt-4 border-t border-[#F1F5F9]">
              <NoteComposer author={user?.name} value={note} onChange={setNote} onSubmit={addNote} staff={staff} />
            </div>
          </div>
        </section>

        <aside className="space-y-6 lg:sticky lg:top-6">
          <TicketAssignCard t={t} isAdmin={user?.role === "admin"} onAssigned={(a) => setT((prev) => ({ ...prev, ...a }))} onTaskSent={(m) => { if (m?.kind === "task" && m.ticket_id === t.id) nav(ticketHref(t.id, { task: m.id }), { replace: true }); }} />
          <div className={`${CARD} p-5`} data-testid="status-card">
            <SectionLabel>Status</SectionLabel>
            <div className="grid grid-cols-2 lg:grid-cols-1 gap-1.5">
              {STATUS_ORDER.map((s) => (
                <button
                  key={s}
                  onClick={() => updateField({ status: s })}
                  data-testid={`set-status-${s}`}
                  className={`w-full flex items-center gap-2.5 px-3 h-10 rounded-xl border text-left transition-colors ${
                    t.status === s ? "border-[#1E5FEB] bg-[#EFF6FF] text-[#1E5FEB] ring-1 ring-[#1E5FEB]" : "border-[#E5E7EB] bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-300"
                  }`}
                >
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: STATUS_COLORS[s] }} />
                  <span className="text-[13px] font-semibold flex-1">{STATUS_LABELS[s]}</span>
                  {t.status === s && <CheckCircle size={15} weight="fill" />}
                </button>
              ))}
            </div>
          </div>

          <div className={`${CARD} p-5`} data-testid="ticket-history">
            <HistoryPanel ticketId={t.id} refreshKey={`${t.updated_at || ""}${(t.notes || []).length}`} />
          </div>

          <div className="px-1 space-y-0.5 text-[11px] text-slate-400">
            <div data-testid="ticket-created-meta">Created {fmtDateTime(t.created_at)}{t.created_by_name ? ` by ${t.created_by_name}` : ""}</div>
            <div>Updated {fmtDateTime(t.updated_at)}</div>
            <div>All times shown in Brisbane (AEST)</div>
          </div>
        </aside>
      </div>
      </div>

      <LabelPrint ticket={t} />
      <PinPromptModal
        open={!!noteAction}
        onClose={() => setNoteAction(null)}
        title={noteAction?.type === "edit" ? "Save note changes?" : "Delete note?"}
        description={noteAction?.type === "edit" ? "Enter your PIN to save. The old and new text are kept in History." : "Enter your PIN to delete. The original text is kept in History."}
        confirmLabel={noteAction?.type === "edit" ? "SAVE NOTE" : "DELETE NOTE"}
        destructive={noteAction?.type === "delete"}
        statusEndpoint={user?.role === "admin" ? "/users/me/pin-status" : "/staff-pin/status"}
        onConfirm={confirmNoteAction}
      />
      <PinPromptModal
        open={pinOpen}
        onClose={() => setPinOpen(false)}
        title="Delete ticket?"
        description={`This permanently removes ${t.ticket_number}. This cannot be undone.`}
        confirmLabel="DELETE TICKET"
        destructive
        statusEndpoint={user?.role === "admin" ? "/users/me/pin-status" : "/staff-pin/status"}
        onConfirm={confirmDelete}
      />
      <SendTicketSmsModal
        open={smsOpen}
        onClose={() => setSmsOpen(false)}
        ticket={t}
        store={store}
        onSent={load}
      />
      <SendTicketEmailModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        ticket={t}
        onSent={load}
      />
      <EditTicketModal
        open={editOpen}
        ticket={t}
        onClose={() => setEditOpen(false)}
        onSaved={(updated) => { setT(updated); setEditOpen(false); loadOnlineInvoices(); }}
      />
      <SendInvoiceModal
        open={sendInvoiceOpen}
        ticket={t}
        linkedSales={linkedSales}
        onClose={() => { setSendInvoiceOpen(false); load(); }}
      />
      {selectedInvoice && (
        <UnifiedInvoiceModal
          invoice={selectedInvoice}
          onClose={() => setSelectedInvoice(null)}
          onUpdated={() => { setSelectedInvoice(null); load(); loadOnlineInvoices(); }}
          onAddPayment={(inv) => { setSelectedInvoice(null); setAddPaymentInvoice(inv); }}
          user={user}
        />
      )}
      {addPaymentInvoice && (
        <AddPaymentModal
          invoice={addPaymentInvoice}
          user={user}
          onClose={() => setAddPaymentInvoice(null)}
          onDone={() => { setAddPaymentInvoice(null); load(); loadOnlineInvoices(); }}
        />
      )}
      {bannerRefundInvoice && (
        <RefundModal
          invoice={bannerRefundInvoice.invoice}
          user={user}
          prefillAmount={bannerRefundInvoice.prefillAmount}
          onClose={() => setBannerRefundInvoice(null)}
          onDone={() => { setBannerRefundInvoice(null); load(); loadOnlineInvoices(); }}
        />
      )}
    </Layout>
  );
}

/**
 * Aggregate what a ticket has actually been billed and paid across EVERY
 * non-voided POS sale attached to it. A ticket should only ever carry one
 * sale; when a second one exists (duplicate billing) looking at just the
 * newest sale made a fully-paid repair read as "SETTLE $399".
 */
export function invoiceOverpaid(oi) {
  const payments = oi.payment_history || [];
  const positive = payments.reduce((s, p) => s + (p.is_refund || p.corrected_at ? 0 : Math.max(0, p.amount)), 0);
  const paid = positive > 0 ? positive : parseFloat(oi.in_store_paid || 0) + parseFloat(oi.stripe_paid_total || 0);
  const net = Math.round((paid - Number(oi.refund_total || 0)) * 100) / 100;
  return Math.round(Math.max(0, net - parseFloat(oi.total || 0)) * 100) / 100;
}

export function ticketBilling(ticket, linkedSales, onlineInvoices = []) {
  const activeSales = (linkedSales?.sales || []).filter((s) => !s.voided);
  const paid = activeSales.reduce((sum, s) => {
    const entries = (s.payments || []).filter((p) => !p.corrected_at && !p.is_refund && Number(p.amount) > 0);
    return sum + entries.reduce((a, p) => a + Number(p.amount || 0), 0);
  }, 0);
  // Duplicate sales bill the same repair, so the cost is the largest sale
  // total, not their sum. A linked invoice raised by a later ticket edit
  // (extra repair added after payment) also counts.
  const invoiceMax = (onlineInvoices || []).filter((oi) => oi.status !== "voided")
    .reduce((max, oi) => Math.max(max, Number(oi.total || 0)), 0);
  const cost = Math.max(activeSales.reduce((max, s) => Math.max(max, Number(s.total || 0)), 0), invoiceMax)
    || Number(ticket?.final_cost || ticket?.estimated_cost || 0);
  const totalPaid = Math.round(paid * 100) / 100;
  const outstanding = Math.round(Math.max(0, cost - totalPaid) * 100) / 100;
  const settledSale = activeSales.find((s) => (s.payments || []).some((p) => !p.corrected_at && Number(p.amount) > 0))
    || activeSales[0] || null;
  return {
    activeSales,
    cost,
    totalPaid,
    outstanding,
    covered: cost > 0 && outstanding < 0.01,
    settledSale,
    isDuplicate: activeSales.length > 1,
  };
}

/**
 * Payment mismatch banner — shown when customer has overpaid or underpaid
 * relative to the current repair cost. Helps staff take immediate action.
 */
function PaymentMismatchBanner({ ticket, onlineInvoices, onOpenInvoice, onRefund, onSendLink }) {
  const activeInvoices = (onlineInvoices || []).filter((oi) => oi.status !== "voided");

  // Use the invoice total (includes accessories) as the expected cost,
  // falling back to estimated_cost if no invoice data
  const invoiceTotal = activeInvoices.reduce((max, oi) => {
    const t = parseFloat(oi.total || 0);
    return t > max ? t : max;
  }, 0);
  const repairCost = invoiceTotal > 0 ? invoiceTotal : Number(ticket.estimated_cost || 0);
  const totalPaid = activeInvoices.reduce((sum, oi) => {
    const payments = oi.payment_history || [];
    // Corrected tenders stay in the ledger for audit but are no longer money
    // the customer paid, so they must never count toward the paid total.
    const fromHistory = payments.reduce((s, p) => s + (p.is_refund || p.corrected_at ? 0 : Math.max(0, p.amount)), 0);
    return sum + (fromHistory > 0 ? fromHistory : (parseFloat(oi.in_store_paid || 0) + parseFloat(oi.stripe_paid_total || 0)));
  }, 0);
  const totalRefunded = activeInvoices.reduce((sum, oi) => sum + Number(oi.refund_total || 0), 0);
  const netPaid = Math.round((totalPaid - totalRefunded) * 100) / 100;
  // Refunds never re-open a balance: owing = cost − gross paid.
  const overpaidBy = Math.round((netPaid - repairCost) * 100) / 100;
  const owing = Math.round((repairCost - totalPaid) * 100) / 100;
  const diff = overpaidBy > 0.005 ? overpaidBy : -Math.max(0, owing);
  const firstInvoice = activeInvoices[0];

  if (activeInvoices.length === 0 || totalPaid < 0.01 || Math.abs(diff) < 0.01) return null;

  const overpaid = diff > 0;

  const nonCashPaid = activeInvoices.reduce((sum, oi) => sum + (oi.payment_history || [])
    .filter((p) => !p.is_refund && !p.corrected_at && p.method !== "cash" && p.source !== "stripe")
    .reduce((s, p) => s + Math.max(0, p.amount), 0), 0);
  if (overpaid && nonCashPaid > repairCost + 0.005) {
    return (
      <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4" data-testid="payment-noncash-overrecorded-banner">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
            <Warning size={20} weight="fill" className="text-amber-600" />
          </div>
          <div className="min-w-0">
            <div className="text-[14px] font-bold text-amber-900">Payment recorded ${money(diff)} above the total</div>
            <div className="text-[12px] text-amber-700 mt-0.5">A card / Tyro / Other payment can&apos;t give change — this is likely a typing mistake. Open the invoice and use Edit on the payment. Don&apos;t refund money that was never received.</div>
          </div>
        </div>
        <button onClick={() => onOpenInvoice(firstInvoice)} className="shrink-0 h-10 px-5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-[12px] font-bold transition-colors" data-testid="banner-open-invoice-edit-payment">
          Open invoice
        </button>
      </div>
    );
  }

  if (overpaid) {
    return (
      <div className="rounded-2xl border-2 border-red-300 bg-red-50 px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4" data-testid="payment-overpaid-banner">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center shrink-0">
            <Warning size={20} weight="fill" className="text-red-600" />
          </div>
          <div className="min-w-0">
            <div className="text-[14px] font-bold text-red-800">
              Customer overpaid by <span className="font-mono">${money(diff)}</span>
            </div>
            <div className="text-[12px] text-red-600 mt-0.5">
              Paid ${money(netPaid)} — repair now costs ${money(repairCost)}. Issue a refund.
            </div>
          </div>
        </div>
        <button
          onClick={() => onRefund(firstInvoice, diff)}
          className="shrink-0 h-10 px-5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-[12px] font-bold flex items-center gap-2 transition-colors"
          data-testid="banner-issue-refund"
        >
          <ArrowCounterClockwise size={14} weight="bold" /> Refund ${money(diff)}
        </button>
      </div>
    );
  }

  // Balance owed is shown (with Send balance link) in the payment rows below.
  return null;
}

/**
 * Compact invoice strip — shows invoice number, payment summary, balance, and "Open" button.
 * No device cards (those are already shown in the ticket's TicketServiceCards above).
 */
function LinkedInvoiceSection({ invoices, onOpenInvoice, onSendLink, totalLabel = "Total" }) {
  const [sendingId, setSendingId] = useState(null);

  return (
    <div className="space-y-3">
      {invoices.filter((oi) => oi.status !== "voided").map((oi) => {
        const isPaid = oi.status === "paid" || (parseFloat(oi.amount_due || oi.total) <= 0.005);
        const amountDue = oi.amount_due != null ? parseFloat(oi.amount_due) : parseFloat(oi.total);
        const payments = (oi.payment_history || []).filter((p) => !p.is_refund && !p.corrected_at && p.amount > 0)
          .map((p) => ({ method: p.method, amount: p.amount, tyro: p.method === "tyro" || !!p.tyro_meta }));
        const overpaidAmt = invoiceOverpaid(oi);
        const showSend = !isPaid && amountDue > 0.005 && oi.payment_token && onSendLink;
        const refunded = Number(oi.refund_total || 0);
        const fullRefund = isFullyRefunded(oi.total, refunded, oi.refund_closed);
        const showCopy = !payments.length && !isPaid && oi.payment_token && ["sent", "viewed"].includes(oi.status);
        return (
          <ReceiptFooter
            key={oi.id}
            testId={`linked-invoice-${oi.invoice_number}`}
            openTestId={`open-invoice-${oi.invoice_number}`}
            label={totalLabel}
            total={parseFloat(oi.total || 0)}
            payments={payments}
            refunded={refunded}
            fullyRefunded={fullRefund}
            balance={isPaid ? 0 : amountDue}
            overpaid={overpaidAmt}
            number={oi.invoice_number}
            statusLabel={fullRefund ? "Refunded" : refunded > 0.005 ? "Part refunded" : overpaidAmt > 0.005 ? "Overpaid" : isPaid ? "Paid" : OI_STATUS_LABELS[oi.status] || oi.status}
            viewed={!!(oi.viewed_at || oi.view_count > 0)}
            viewCount={oi.view_count}
            onOpen={() => onOpenInvoice(oi)}
            actions={<>
              {showCopy && (
                <button onClick={() => copyInvoiceLink(oi)} className="inline-flex items-center gap-1 text-[12px] font-semibold text-slate-600 hover:text-[#1E5FEB] transition-colors" data-testid={`copy-link-${oi.invoice_number}`}>
                  <CopySimple size={12} weight="bold" /> Copy link
                </button>
              )}
              {showSend && (
                <button
                  onClick={async () => {
                    setSendingId(oi.id);
                    try { await onSendLink(oi); }
                    catch (e) { toast.error(formatApiError(e, "Failed to send balance link")); }
                    finally { setSendingId(null); }
                  }}
                  disabled={sendingId === oi.id}
                  className="inline-flex items-center gap-1 text-[12px] font-semibold text-amber-700 hover:text-amber-800 disabled:opacity-60 transition-colors"
                  data-testid={`send-balance-link-${oi.invoice_number}`}
                >
                  <PaperPlaneTilt size={12} weight="bold" /> {sendingId === oi.id ? "Sending…" : "Send balance link"}
                </button>
              )}
            </>}
          />
        );
      })}
    </div>
  );
}

/**
 * Parse "• Repair Name ($119.00)\n• Another ($200.00)" into [{name, price}]
 */
function parseIssueToRepairs(issue) {
  if (!issue) return [];
  return issue.split('\n')
    .map(l => l.trim())
    .filter(l => l.startsWith('•'))
    .map(l => {
      const m = l.match(/^•\s*(.+?)\s*\(\$?([\d,.]+)\)\s*$/);
      if (m) return { name: m[1].trim(), price: parseFloat(m[2].replace(',', '')) };
      return { name: l.replace(/^•\s*/, '').trim(), price: 0 };
    });
}

/**
 * Service cards — shows all devices with their repairs in invoice-style cards.
 * Primary device is card #1, additional devices follow.
 */
function TicketServiceCards({ ticket: t, renderTotal }) {
  const deviceGroups = ticketDeviceGroups(t);
  const accessories = Array.isArray(t.pending_accessories) && t.pending_accessories.length > 0
    ? t.pending_accessories
    : Array.isArray(t.accessories) && t.accessories.length > 0
      ? t.accessories
      : [];

  const totalDevices = deviceGroups.length;

  return (
    <div className="space-y-3" data-testid="service-cards">
      {deviceGroups.map((device, idx) => {
        const deviceCost = device.repairs.reduce((sum, repair) => sum + Number(repair.price || 0), 0);
        return (
          <div key={device.key} className="rounded-2xl border border-[#E5E7EB] overflow-hidden bg-white" data-testid={`device-card-${idx}`}>
            <div className="px-4 py-3 bg-[#FAFBFC] border-b border-[#E5E7EB] flex items-center justify-between gap-3">
              <DeviceIdentity index={idx + 1} make={device.make} type={device.type} model={device.model} year={device.year} modelNumber={device.modelNumber} />
              <div className="flex items-center gap-2 shrink-0">
                {device.imei && device.imei !== "—" && (
                  <span className="hidden sm:inline-flex text-[9px] font-mono text-[#9CA3AF] bg-slate-100 px-2 py-0.5 rounded" data-testid={`device-imei-${idx}`}>IMEI {device.imei}</span>
                )}
                <span className="font-mono text-[14px] font-bold text-[#121216] tabular-nums">
                  ${money(deviceCost)}
                </span>
              </div>
            </div>
            {device.repairs.length > 0 ? device.repairs.map((r, ri) => (
              <div key={ri} className="flex items-center justify-between px-5 py-2.5 border-b border-[#F3F4F6] last:border-0">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="w-2 h-2 rounded-full bg-[#1E5FEB] shrink-0" />
                  <span className="text-[13px] text-[#121216]">{r.name}</span>
                </div>
                <span className="font-mono text-[13px] text-[#9CA3AF] tabular-nums shrink-0 ml-4">
                  ${money(r.price || 0)}
                </span>
              </div>
            )) : <div className="px-5 py-2.5 text-[13px] text-[#9CA3AF] italic">No repair items listed</div>}
          </div>
        );
      })}

      {/* ── Accessories Card ── */}
      {accessories.length > 0 && (
        <div className="rounded-2xl border border-[#E5E7EB] overflow-hidden bg-white" data-testid="accessories-card">
          <div className="px-4 py-2.5 bg-amber-50/60 border-b border-[#E5E7EB] flex items-center gap-2.5">
            <div className="w-6 h-6 rounded-md bg-amber-200 flex items-center justify-center shrink-0 text-[9px] font-bold text-amber-800">A</div>
            <span className="text-[12px] font-bold text-amber-800 uppercase tracking-wider">Accessories</span>
          </div>
          {accessories.map((a, ai) => (
            <div key={ai} className="flex items-center justify-between px-5 py-2.5 border-b border-[#F3F4F6] last:border-0">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0" />
                <span className="text-[13px] text-[#121216]">{a.name || a.description || "Accessory"}</span>
                {(a.quantity || 1) > 1 && <span className="text-[11px] text-[#9CA3AF]">x{a.quantity}</span>}
              </div>
              <span className="font-mono text-[13px] text-[#9CA3AF] tabular-nums shrink-0 ml-4">
                ${money(a.line_total || a.unit_price || a.price || 0)}
              </span>
            </div>
          ))}
        </div>
      )}

      {renderTotal(`Total${totalDevices > 1 ? ` · ${totalDevices} devices` : ""}${accessories.length > 0 ? ` + ${accessories.length} accessor${accessories.length === 1 ? "y" : "ies"}` : ""}`)}
    </div>
  );
}

/**
 * Full-edit modal for a repair ticket with structured repair line items.
 * Staff can:
 *   • Swap customer (search by name / phone / email)
 *   • Pick make/model from catalog dropdowns
 *   • Select repairs from catalog with original prices
 *   • Add multiple repairs, edit prices per line
 *   • Add custom (non-catalog) repairs
 *   • Auto-sum estimated cost from repair lines
 */
function EditTicketModal({ open, ticket, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [custQuery, setCustQuery] = useState("");
  const [custResults, setCustResults] = useState([]);
  const [custPicking, setCustPicking] = useState(false);
  const [saving, setSaving] = useState(false);
  // Structured repair lines: [{ name, catalog_price, price }]
  const [repairLines, setRepairLines] = useState([]);
  const [checklistTouched, setChecklistTouched] = useState(false);
  // Website catalog for repair lookups
  const [websiteCatalog, setWebsiteCatalog] = useState(null);
  // Local repair catalog entries
  const [localCatalog, setLocalCatalog] = useState(null);
  // Custom repair input state
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customName, setCustomName] = useState("");
  const [customPrice, setCustomPrice] = useState("");
  // Make/Model dropdown state
  const [makeDropdownOpen, setMakeDropdownOpen] = useState(false);
  const [makeSearch, setMakeSearch] = useState("");
  const [deviceTypeDropdownOpen, setDeviceTypeDropdownOpen] = useState(false);
  const [deviceTypeSearch, setDeviceTypeSearch] = useState("");
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const [modelSearch, setModelSearch] = useState("");
  // Additional devices for multi-device editing
  const [additionalDevices, setAdditionalDevices] = useState([]);
  // Empty device identities may be left behind when staff replace the only
  // repair on an older ticket. Keep removals local until Save so Cancel
  // remains safe.
  const [removedDeviceKeys, setRemovedDeviceKeys] = useState([]);
  // Which device Add New / Custom / IMEI fields apply to: "primary" or an
  // additional device's _key. Each repair line carries the same device_ref.
  const [targetRef, setTargetRef] = useState("primary");
  const [touchedRefs, setTouchedRefs] = useState([]);
  // Accessories captured at intake (or added at checkout). Editable here so
  // staff can fix a mis-keyed case / screen protector without voiding the
  // ticket. `accField` records which array the ticket is actually using so
  // we write back to the same one the detail page renders.
  const [accessories, setAccessories] = useState([]);
  const [accField, setAccField] = useState("pending_accessories");
  const [accSearch, setAccSearch] = useState("");
  const [accResults, setAccResults] = useState([]);
  const [accOpen, setAccOpen] = useState(false);
  // Full-screen Brand → Device → Model → Issue wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  // When set, the wizard replaces the line at this index instead of appending
  const [wizardReplaceIdx, setWizardReplaceIdx] = useState(null);

  // Fetch website catalog once
  useEffect(() => {
    if (!open) return;
    api.get("/catalog/website").then((r) => setWebsiteCatalog(r.data)).catch(() => {});
    api.get("/repair-catalog").then((r) => setLocalCatalog(r.data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!ticket) return;
    setForm({
      customer_id: ticket.customer_id || "",
      customer_name: ticket.customer_name || "",
      customer_phone: ticket.customer_phone || "",
      device_make: ticket.device_make || "",
      device_type: ticket.device_type || "",
      device_model: ticket.device_model || "",
      device_year: ticket.device_year || "",
      device_model_number: ticket.device_model_number || "",
      imei: ticket.imei || "",
      passcode: ticket.passcode || "",
      issue: ticket.issue || "",
      estimated_cost: ticket.estimated_cost ?? 0,
      final_cost: ticket.final_cost ?? 0,
      warranty_months: ticket.warranty_months || 12,
    });
    // Initialize repair lines from ticket data. Older tickets stored their
    // part key only in intake_checklist, so preserve its line ordering until
    // a cashier explicitly swaps or removes a repair.
    const existingStockKeys = Object.keys(ticket.intake_checklist || {});
    if (ticket.repair_lines && ticket.repair_lines.length > 0) {
      setRepairLines(ticket.repair_lines.map((rl, i) => ({
        ...rl,
        stock_key: rl.stock_key || existingStockKeys[i] || null,
      })));
    } else if (ticket.issue) {
      // Legacy website conversions appended "Notes: ..." to the issue. A
      // customer comment is never a repair line and must not split the quote.
      const repairOnlyIssue = ticket.issue.split(/\n\s*notes?\s*:/i)[0].trim();
      // Parse existing bullet-point issue text into lines
      const lines = repairOnlyIssue.split("\n")
        .map((l) => l.replace(/^[•\-\*]\s*/, "").trim())
        .filter(Boolean);
      if (lines.length > 0) {
        const perLine = lines.length > 1 ? (ticket.estimated_cost || 0) / lines.length : ticket.estimated_cost || 0;
        setRepairLines(lines.map((name, i) => ({
          name,
          catalog_price: null,
          price: Math.round(perLine * 100) / 100,
          stock_key: existingStockKeys[i] || null,
        })));
      } else {
        setRepairLines([]);
      }
    } else {
      setRepairLines([]);
    }
    setCustQuery("");
    setCustResults([]);
    setCustPicking(false);
    setShowCustomInput(false);
    setMakeDropdownOpen(false);
    setDeviceTypeDropdownOpen(false);
    setModelDropdownOpen(false);
    // Initialize additional devices. Their repairs are edited in the same
    // Repairs table (tagged with device_ref) so every device is editable and
    // the estimated total always covers all devices.
    const extras = (ticket.additional_devices || []).map((d, i) => ({
      ...d,
      _key: `dev-${i}`,
      device_make: d.device_make || "",
      device_type: d.device_type || "",
      device_model: d.device_model || "",
      device_year: d.device_year || "",
      imei: d.imei || "",
      passcode: d.passcode || "",
      issue: d.issue || "",
      estimated_cost: d.estimated_cost ?? 0,
      intake_checklist: d.intake_checklist || {},
    }));
    setAdditionalDevices(extras);
    setTargetRef("primary");
    setTouchedRefs([]);
    setRepairLines((prev) => {
      const primaryModel = deviceModelKey(ticket.device_make, ticket.device_model);
      const refForKey = (stockKey) => {
        const model = modelFromStockKey(stockKey);
        if (model && model === primaryModel) return "primary";
        const hit = model && extras.find((d) => deviceModelKey(d.device_make, d.device_model) === model);
        return hit ? hit._key : "primary";
      };
      const tagged = prev.map((rl) => ({ ...rl, device_ref: refForKey(rl.stock_key) }));
      extras.filter(hasDeviceIdentity).forEach((d) => {
        if (tagged.some((rl) => rl.device_ref === d._key)) return;
        const parsed = parseIssueToRepairs(d.issue);
        const keys = Object.keys(d.intake_checklist || {});
        if (parsed.length === 1 && !parsed[0].price) parsed[0].price = Number(d.estimated_cost) || 0;
        parsed.forEach((r, i) => tagged.push({
          name: r.name,
          catalog_price: null,
          price: r.price,
          stock_key: keys.length === parsed.length ? keys[i] : null,
          device_ref: d._key,
        }));
      });
      return tagged;
    });
    // Seed accessories from whichever array the ticket is using. Before
    // checkout they live in `pending_accessories`; create_sale moves them
    // into `accessories`. Mirror the detail page's precedence so the editor
    // always shows exactly what the ticket card shows.
    const pend = Array.isArray(ticket.pending_accessories) ? ticket.pending_accessories : [];
    const settled = Array.isArray(ticket.accessories) ? ticket.accessories : [];
    const src = pend.length > 0 ? pend : settled;
    setAccField(pend.length > 0 || settled.length === 0 ? "pending_accessories" : "accessories");
    setAccessories(src.map((a, i) => ({
      _key: `acc-${i}`,
      product_id: a.product_id || null,
      sku: a.sku || "",
      name: a.name || "",
      quantity: Number(a.quantity || 1),
      unit_price: Number(a.unit_price ?? a.price ?? 0),
      kind: a.kind || "product",
    })));
    setAccSearch("");
    setAccResults([]);
    setAccOpen(false);
    setChecklistTouched(false);
    setRemovedDeviceKeys([]);
  }, [ticket, open]);

  // Debounced product search for the accessory picker
  useEffect(() => {
    const q = accSearch.trim();
    if (!open || q.length < 1) { setAccResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/products", { params: { q, limit: 20 } });
        setAccResults((data || []).slice(0, 8));
      } catch { setAccResults([]); }
    }, 180);
    return () => clearTimeout(t);
  }, [accSearch, open]);

  // Debounced customer search
  useEffect(() => {
    if (!custPicking || custQuery.trim().length < 2) {
      setCustResults([]);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get("/customers", { params: { search: custQuery.trim(), limit: 8 } });
        setCustResults(data || []);
      } catch { setCustResults([]); }
    }, 250);
    return () => clearTimeout(handle);
  }, [custQuery, custPicking]);

  // Build available makes list from catalogs
  const availableMakes = useMemo(() => {
    const makes = new Map();
    // From website catalog
    (websiteCatalog?.manufacturers || []).forEach((m) => {
      makes.set(m.name.toLowerCase(), m.name);
    });
    // From static catalog
    MANUFACTURERS.forEach((m) => {
      if (!makes.has(m.label.toLowerCase())) makes.set(m.label.toLowerCase(), m.label);
    });
    // From local repair catalog
    (localCatalog?.manufacturer || []).forEach((m) => {
      if (!makes.has((m.label || "").toLowerCase())) makes.set((m.label || "").toLowerCase(), m.label);
    });
    return Array.from(makes.values()).sort();
  }, [websiteCatalog, localCatalog]);

  // Build available device types for selected make
  const availableDeviceTypes = useMemo(() => {
    if (!form?.device_make) return [];
    const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
    const makeKey = norm(form.device_make);
    const types = new Map();

    // From website catalog
    const wMfr = (websiteCatalog?.manufacturers || []).find((m) => norm(m.name) === makeKey);
    if (wMfr) {
      (wMfr.device_types || []).forEach((dt) => {
        types.set(norm(dt.name), dt.name);
      });
    }

    // From static catalog
    const staticMfr = MANUFACTURERS.find((m) => norm(m.label) === makeKey);
    if (staticMfr) {
      DEVICE_TYPES.forEach((dt) => {
        const key = `${staticMfr.id}|${dt.id}`;
        if (MODELS[key]?.length > 0 && !types.has(norm(dt.label))) {
          types.set(norm(dt.label), dt.label);
        }
      });
    }

    // Always include common types if none found
    if (types.size === 0) {
      DEVICE_TYPES.forEach((dt) => types.set(norm(dt.label), dt.label));
    }

    return Array.from(types.values()).sort();
  }, [form?.device_make, websiteCatalog, localCatalog]);

  // Build available models list for selected make + device type
  const availableModels = useMemo(() => {
    if (!form?.device_make) return [];
    const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
    const makeKey = norm(form.device_make);
    const typeKey = form.device_type ? norm(form.device_type) : "";
    const models = new Map();

    // From website catalog
    const wMfr = (websiteCatalog?.manufacturers || []).find((m) => norm(m.name) === makeKey);
    if (wMfr) {
      (wMfr.device_types || []).forEach((dt) => {
        // Filter by device type if selected
        if (typeKey && norm(dt.name) !== typeKey) return;
        (dt.models || []).forEach((md) => {
          const label = md.year ? `${md.name} · ${md.year}` : md.name;
          models.set(norm(md.name), { label, name: md.name, year: md.year || "" });
        });
      });
    }

    // From static catalog
    const staticMfr = MANUFACTURERS.find((m) => norm(m.label) === makeKey);
    if (staticMfr) {
      DEVICE_TYPES.forEach((dt) => {
        if (typeKey && norm(dt.label) !== typeKey) return;
        const key = `${staticMfr.id}|${dt.id}`;
        (MODELS[key] || []).forEach((m) => {
          const k = norm(m.label || m);
          if (!models.has(k)) {
            models.set(k, { label: typeof m === "string" ? m : m.label, name: typeof m === "string" ? m : m.label, year: "" });
          }
        });
      });
    }

    return Array.from(models.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [form?.device_make, form?.device_type, websiteCatalog, localCatalog]);

  // Auto-calculate estimated cost from repair lines
  const lineTotal = useMemo(() => {
    return repairLines.reduce((sum, rl) => sum + (Number(rl.price) || 0), 0);
  }, [repairLines]);

  // Sync estimated_cost and final_cost when repair lines change
  useEffect(() => {
    if (repairLines.length > 0 && form) {
      const rounded = Math.round(lineTotal * 100) / 100;
      setForm((prev) => prev ? { ...prev, estimated_cost: rounded, final_cost: rounded } : prev);
    }
  }, [lineTotal]);

  if (!open || !form) return null;

  const setField = (k) => (e) => setForm({ ...form, [k]: e?.target?.value ?? e });

  const addCustomRepair = () => {
    if (!customName.trim()) return;
    setRepairLines((prev) => [...prev, {
      name: customName.trim(),
      catalog_price: null,
      price: Number(customPrice) || 0,
      device_ref: targetRef,
    }]);
    setTouchedRefs((refs) => [...new Set([...refs, targetRef])]);
    setCustomName("");
    setCustomPrice("");
    setShowCustomInput(false);
  };

  const updateLinePrice = (idx, newPrice) => {
    setRepairLines((prev) => prev.map((rl, i) => i === idx ? { ...rl, price: Number(newPrice) || 0 } : rl));
  };

  const removeLine = (idx) => {
    setRepairLines((prev) => {
      if (prev[idx]?.stock_key) setChecklistTouched(true);
      const ref = prev[idx]?.device_ref || "primary";
      setTouchedRefs((refs) => [...new Set([...refs, ref])]);
      return prev.filter((_, i) => i !== idx);
    });
  };

  // ── Accessory helpers ──────────────────────────────────────────────
  const accTotal = accessories.reduce(
    (s, a) => s + Number(a.unit_price || 0) * Number(a.quantity || 0), 0
  );

  const addAcc = (p) => {
    if (!p) return;
    setAccessories((arr) => {
      const hit = arr.find((a) => a.product_id && a.product_id === p.id);
      if (hit) {
        return arr.map((a) => a === hit ? { ...a, quantity: a.quantity + 1 } : a);
      }
      return [...arr, {
        _key: `acc-new-${Date.now()}`,
        product_id: p.id,
        sku: p.sku || "",
        name: p.name,
        quantity: 1,
        unit_price: Number(p.price || 0),
        kind: p.kind === "accessory" ? "product" : (p.kind || "product"),
      }];
    });
    setAccSearch("");
    setAccResults([]);
    setAccOpen(false);
  };

  const addCustomAcc = () => {
    setAccessories((arr) => [...arr, {
      _key: `acc-new-${Date.now()}`,
      product_id: null, sku: "", name: "", quantity: 1, unit_price: 0, kind: "custom",
    }]);
  };

  const patchAcc = (key, patch) =>
    setAccessories((arr) => arr.map((a) => a._key === key ? { ...a, ...patch } : a));

  const removeAcc = (key) =>
    setAccessories((arr) => arr.filter((a) => a._key !== key));

  // Selecting an issue in the catalog wizard either appends a new repair
  // line OR replaces the line at wizardReplaceIdx. Also auto-fills the
  // ticket's Make/Type/Model when those are still empty — saves the
  // cashier from re-picking the same device twice.
  const handleWizardSelect = (payload) => {
    const ref = wizardReplaceIdx != null ? (repairLines[wizardReplaceIdx]?.device_ref || "primary") : targetRef;
    const line = {
      name: payload.description,
      catalog_price: payload.unit_price || 0,
      price: payload.unit_price || 0,
      stock_key: payload.stock_key || null,
      device_ref: ref,
    };
    if (wizardReplaceIdx != null) {
      setRepairLines((prev) => prev.map((rl, i) => (i === wizardReplaceIdx ? { ...rl, ...line } : rl)));
    } else {
      setRepairLines((prev) => [...prev, line]);
    }
    setTouchedRefs((refs) => [...new Set([...refs, ref])]);
    if (ref !== "primary") {
      setAdditionalDevices((devs) => devs.map((d) => (d._key !== ref ? d : {
        ...d,
        device_make: payload.brand_label || d.device_make,
        device_type: payload.device_type_label || d.device_type,
        device_model: payload.model || d.device_model,
        device_year: payload.model_year || "",
        device_model_number: payload.model_number || "",
      })));
      setChecklistTouched(true);
      setWizardOpen(false);
      setWizardReplaceIdx(null);
      return;
    }
    // The selected catalog repair is the authoritative device identity. This
    // keeps the ticket header, receipt and reserved part aligned whether the
    // old repair was swapped or deleted before choosing Add New.
    setForm((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      if (payload.brand_label) next.device_make = payload.brand_label;
      if (payload.device_type_label) next.device_type = payload.device_type_label;
      if (payload.model) next.device_model = payload.model;
      next.device_year = payload.model_year || "";
      next.device_model_number = payload.model_number || "";
      return next;
    });
    setChecklistTouched(true);
    setWizardOpen(false);
    setWizardReplaceIdx(null);
  };

  const selectMake = (makeName) => {
    setForm({ ...form, device_make: makeName, device_type: "", device_model: "" });
    setMakeDropdownOpen(false);
    setMakeSearch("");
  };

  const selectDeviceType = (typeName) => {
    setForm({ ...form, device_type: typeName, device_model: "" });
    setDeviceTypeDropdownOpen(false);
    setDeviceTypeSearch("");
  };

  const selectModel = (modelName) => {
    setForm({ ...form, device_model: modelName });
    setModelDropdownOpen(false);
    setModelSearch("");
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const linesFor = (ref) => repairLines.filter((rl) => (rl.device_ref || "primary") === ref);
      const stripRef = ({ device_ref, ...rl }) => rl;
      const bullets = (lines) => lines.map((rl) => `• ${rl.name} ($${(Number(rl.price) || 0).toFixed(2)})`).join("\n");
      const primaryLines = linesFor("primary").map(stripRef);
      let extrasWithoutLines = 0;
      const keptExtras = additionalDevices
        .filter(hasDeviceIdentity)
        .filter((device) => !removedDeviceKeys.includes(device._key))
        .map(({ _key, ...d }) => {
          const lines = linesFor(_key);
          const touched = touchedRefs.includes(_key);
          if (lines.length === 0 && !touched) {
            extrasWithoutLines += Number(d.estimated_cost) || 0;
            return { ...d, estimated_cost: Number(d.estimated_cost) || 0 };
          }
          const cost = Math.round(lines.reduce((a, rl) => a + (Number(rl.price) || 0), 0) * 100) / 100;
          return {
            ...d,
            issue: bullets(lines),
            estimated_cost: cost,
            final_cost: cost,
            intake_checklist: touched
              ? lines.reduce((c, rl) => { if (rl.stock_key) c[rl.stock_key] = true; return c; }, {})
              : (d.intake_checklist || {}),
          };
        });
      const allTotal = Math.round((lineTotal + extrasWithoutLines) * 100) / 100;
      const payload = {
        customer_id: form.customer_id || null,
        customer_name: form.customer_name,
        customer_phone: form.customer_phone,
        device_make: form.device_make,
        device_type: form.device_type,
        device_model: form.device_model,
        device_year: form.device_year,
        device_model_number: form.device_model_number,
        imei: form.imei,
        passcode: form.passcode,
        repair_lines: primaryLines.length > 0 ? primaryLines : (repairLines.length > 0 ? [] : null),
        issue: primaryLines.length > 0 ? bullets(primaryLines) : (repairLines.length > 0 ? "" : form.issue),
        // Ticket total = every device's repairs (matches intake roll-up).
        estimated_cost: repairLines.length > 0 ? allTotal : Number(form.estimated_cost) || 0,
        final_cost: repairLines.length > 0 ? allTotal : Number(form.final_cost) || 0,
        warranty_months: Number(form.warranty_months) || 0,
        // Blank legacy Device #2 placeholders add no service history and
        // confuse staff. Preserve real secondary devices, but clean those
        // empty rows the next time a ticket is saved.
        additional_devices: keptExtras,
      };
      if (checklistTouched) {
        payload.intake_checklist = linesFor("primary").reduce((checklist, line) => {
          if (line.stock_key) checklist[line.stock_key] = true;
          return checklist;
        }, {});
      }
      // Write accessories back to the same array the ticket is using so the
      // detail card, invoice total and the POS cart stay in agreement.
      payload[accField] = accessories
        .filter((a) => (a.name || "").trim())
        .map((a) => ({
          product_id: a.product_id || null,
          sku: a.sku || "",
          name: a.name.trim(),
          quantity: Number(a.quantity) || 1,
          unit_price: Number(a.unit_price) || 0,
          line_total: Math.round((Number(a.unit_price) || 0) * (Number(a.quantity) || 1) * 100) / 100,
          kind: a.kind || "product",
        }));
      const res = await api.patch(`/repair-tickets/${ticket.id}`, payload);
      // Highlight to staff when the ticket edit also repriced a linked
      // invoice (deposit link, sent invoice, etc.) so they don't send a
      // stale amount to the customer by mistake.
      const notInStock = parseInt(res.headers?.["x-parts-not-in-stock"] || "0", 10);
      if (notInStock > 0) {
        toast.warning(`${notInStock} repair part${notInStock === 1 ? " isn't" : "s aren't"} in stock — nothing deducted for ${notInStock === 1 ? "it" : "them"}. Add stock in Parts Stock.`, { duration: 6000 });
      }
      const syncCount = parseInt(res.headers?.["x-invoice-sync-count"] || "0", 10);
      const syncDelta = parseFloat(res.headers?.["x-invoice-sync-delta"] || "0");
      if (syncCount > 0) {
        const dir = syncDelta > 0.005 ? "raised" : syncDelta < -0.005 ? "lowered" : "updated";
        const amt = Math.abs(syncDelta).toFixed(2);
        toast.success(
          `Ticket saved · ${syncCount} linked invoice ${syncDelta === 0 ? "resynced" : `${dir} by $${amt}`}`,
          { duration: 5000 }
        );
      } else {
        toast.success("Ticket saved");
      }
      onSaved?.(res.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const fld = "w-full h-10 px-3 text-[13px] rounded-lg border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none";

  // Filter makes for dropdown
  const filteredMakes = makeSearch
    ? availableMakes.filter((m) => m.toLowerCase().includes(makeSearch.toLowerCase()))
    : availableMakes;

  // Filter device types for dropdown
  const filteredDeviceTypes = deviceTypeSearch
    ? availableDeviceTypes.filter((t) => t.toLowerCase().includes(deviceTypeSearch.toLowerCase()))
    : availableDeviceTypes;

  // Filter models for dropdown
  const filteredModels = modelSearch
    ? availableModels.filter((m) => m.label.toLowerCase().includes(modelSearch.toLowerCase()))
    : availableModels;
  // Always render from the current draft — after a repair is replaced via
  // Add New, `form` holds its new device model even before Save. Reading
  // the stale ticket here previously showed the old empty model as Device 1.
  const editingDeviceGroups = ticketDeviceGroups({
    ...ticket,
    device_make: form.device_make,
    device_type: form.device_type,
    device_model: form.device_model,
    device_year: form.device_year,
    device_model_number: form.device_model_number,
    imei: form.imei,
    passcode: form.passcode,
    additional_devices: additionalDevices.map((d) => {
      const lines = repairLines.filter((rl) => rl.device_ref === d._key);
      return lines.length || touchedRefs.includes(d._key)
        ? { ...d, issue: lines.map((rl) => `• ${rl.name} ($${(Number(rl.price) || 0).toFixed(2)})`).join("\n") }
        : d;
    }),
    repair_lines: repairLines.filter((rl) => (rl.device_ref || "primary") === "primary"),
    issue: repairLines.length > 0 ? "" : ticket.issue,
  });
  const refForGroup = (group) => group.ref || "primary";
  const targetDevice = targetRef === "primary" ? null : additionalDevices.find((d) => d._key === targetRef);
  const setTargetField = (k) => (e) => {
    const v = e?.target?.value ?? e;
    if (!targetDevice) setForm({ ...form, [k]: v });
    else setAdditionalDevices((devs) => devs.map((d) => (d._key === targetRef ? { ...d, [k]: v } : d)));
  };
  const lineDeviceLabel = (rl) => {
    const dev = rl.device_ref && rl.device_ref !== "primary" && additionalDevices.find((d) => d._key === rl.device_ref);
    const label = dev ? modelOnly(dev.device_make, dev.device_model) : deviceLabelForRepair(rl, visibleDeviceGroups);
    if (visibleDeviceGroups.length < 2) return label;
    const idx = visibleDeviceGroups.findIndex((g) => refForGroup(g) === (rl.device_ref || "primary"));
    return idx >= 0 ? `#${idx + 1} ${label}` : label;
  };
  const visibleDeviceGroups = editingDeviceGroups.filter((device) => !removedDeviceKeys.includes(device.key));

  const promoteDevice = (device) => {
    let catalogIdentity = null;
    (websiteCatalog?.manufacturers || []).some((manufacturer) =>
      (manufacturer.device_types || []).some((deviceType) =>
        (deviceType.models || []).some((model) => {
          if (model.id !== device.key) return false;
          catalogIdentity = {
            make: manufacturer.name || "",
            type: deviceType.name || "",
            model: model.name || "",
            year: model.year || "",
            modelNumber: model.model_number || "",
          };
          return true;
        })
      )
    );
    setForm((prev) => ({
      ...prev,
      device_make: catalogIdentity?.make || device.make || prev.device_make,
      device_type: catalogIdentity?.type || device.type || prev.device_type,
      device_model: catalogIdentity?.model || device.model || prev.device_model,
      device_year: catalogIdentity?.year || device.year || "",
      device_model_number: catalogIdentity?.modelNumber || device.modelNumber || "",
      imei: device.imei || "",
      passcode: device.passcode || "",
    }));
  };

  const removeEmptyDevice = (device) => {
    if (device.repairs.length > 0) return;
    const isDraftPrimary = device.ref === "primary";
    const replacement = visibleDeviceGroups.find((candidate) => candidate.key !== device.key && candidate.repairs.length > 0);
    if (isDraftPrimary && replacement) {
      const ref = refForGroup(replacement);
      if (ref !== "primary") {
        // An additional device becomes the primary: move its repairs and
        // drop it from the extras so it isn't saved twice.
        const extra = additionalDevices.find((d) => d._key === ref);
        setRepairLines((lines) => lines.map((rl) => (rl.device_ref === ref ? { ...rl, device_ref: "primary" } : rl)));
        setAdditionalDevices((devs) => devs.filter((d) => d._key !== ref));
        setForm((prev) => ({ ...prev, device_make: extra.device_make, device_type: extra.device_type, device_model: extra.device_model,
          device_year: extra.device_year || "", device_model_number: extra.device_model_number || "", imei: extra.imei || "", passcode: extra.passcode || "" }));
        setChecklistTouched(true);
        setTargetRef("primary");
      } else {
        promoteDevice(replacement);
      }
    } else {
      setRemovedDeviceKeys((keys) => [...new Set([...keys, device.key, ...(device.ref ? [device.ref] : [])])]);
      setTargetRef("primary");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/55 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-2xl max-h-[92vh] flex flex-col border border-[#E5E7EB] rounded-2xl" data-testid="edit-ticket-modal">
        <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between shrink-0">
          <div>
            <div className="overline mb-1">EDIT TICKET · {ticket.ticket_number}</div>
            <h2 className="font-display font-semibold text-[20px] tracking-tight">Update repair details</h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-[#F8FAFC] rounded-full"><X size={18} weight="bold" /></button>
        </div>

        <div className="p-6 space-y-5 overflow-y-auto">
          {/* Customer block */}
          <section>
            <div className="overline mb-2">CUSTOMER</div>
            {!custPicking ? (
              <div className="flex items-center justify-between bg-[#F8FAFC] border border-[#E5E7EB] rounded-lg p-3">
                <div>
                  <div className="font-semibold text-[14px] text-[#121216]">{form.customer_name || "— none —"}</div>
                  {form.customer_phone && <div className="text-[12px] font-mono text-[#4B5563]">{form.customer_phone}</div>}
                </div>
                <button
                  type="button"
                  onClick={() => { setCustPicking(true); setCustQuery(""); }}
                  className="h-8 px-3 text-[11px] font-semibold border border-[#E5E7EB] hover:border-[#1E5FEB] hover:text-[#1E5FEB] rounded-full"
                  data-testid="swap-customer-btn"
                >CHANGE</button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="relative">
                  <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                  <input
                    autoFocus
                    value={custQuery}
                    onChange={(e) => setCustQuery(e.target.value)}
                    placeholder="Search by name / phone / email…"
                    className={`${fld} pl-8`}
                    data-testid="customer-search"
                  />
                  <button type="button" onClick={() => setCustPicking(false)} className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-[#9CA3AF] hover:text-[#121216]">Cancel</button>
                </div>
                {custResults.length > 0 && (
                  <div className="border border-[#E5E7EB] rounded-lg divide-y divide-[#E5E7EB] bg-white max-h-48 overflow-y-auto">
                    {custResults.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => {
                          setForm({ ...form, customer_id: c.id, customer_name: c.name, customer_phone: c.phone || "" });
                          setCustPicking(false);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-[#F8FAFC] flex items-center justify-between"
                        data-testid={`customer-pick-${c.id}`}
                      >
                        <div>
                          <div className="text-[13px] font-semibold text-[#121216]">{c.name}</div>
                          <div className="text-[11px] font-mono text-[#4B5563]">{c.phone || c.email || "—"}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {custQuery.trim().length >= 2 && custResults.length === 0 && (
                  <div className="text-[11px] text-[#9CA3AF]">No matches — keep typing or cancel to keep the current customer.</div>
                )}
              </div>
            )}
          </section>

          {/* Device block — repair stock keys may point at more than one model. */}
          <section>
            <div className="overline mb-2">DEVICES ON THIS TICKET</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3" data-testid="edit-ticket-device-list">
              {visibleDeviceGroups.map((device, index) => (
                <div
                  key={device.key}
                  role="button"
                  tabIndex={0}
                  onClick={() => setTargetRef(refForGroup(device))}
                  onKeyDown={(e) => { if (e.key === "Enter") setTargetRef(refForGroup(device)); }}
                  className={`border px-3 py-2.5 cursor-pointer transition-colors ${refForGroup(device) === targetRef ? "border-[#1E5FEB] bg-[#EEF4FF] ring-2 ring-[#1E5FEB]/20" : "border-[#E5E7EB] bg-[#F8FAFC] hover:border-[#93B4F5]"}`}
                  data-testid={`edit-ticket-device-${index}`}
                  aria-pressed={refForGroup(device) === targetRef}
                >
                  <div className="flex items-center gap-2">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#121216] text-[9px] font-bold text-white">{index + 1}</span>
                    <span className="min-w-0 flex-1 truncate text-[14px] font-semibold text-[#121216]">{modelOnly(device.make, device.model)}</span>
                    {device.repairs.length === 0 && visibleDeviceGroups.length > 1 && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); removeEmptyDevice(device); }}
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded border border-[#FCA5A5] text-[#DC2626] transition-colors hover:bg-red-50"
                        data-testid={`remove-empty-device-${index}`}
                        title="Remove empty device"
                      >
                        <X size={13} weight="bold" />
                      </button>
                    )}
                  </div>
                  <div className="mt-1 pl-7 text-[10px] text-[#64748B]">{device.repairs.length} repair{device.repairs.length === 1 ? "" : "s"} · ${money(device.repairs.reduce((sum, repair) => sum + Number(repair.price || 0), 0))}</div>
                </div>
              ))}
            </div>
            {visibleDeviceGroups.length > 1 && (
              <div className="mb-2 text-[11px] text-[#64748B]" data-testid="edit-ticket-target-hint">
                Editing <span className="font-semibold text-[#1E5FEB]">{targetDevice ? modelOnly(targetDevice.device_make, targetDevice.device_model) : modelOnly(form.device_make, form.device_model)}</span> — tap a device to switch. Add New / Custom repairs go to this device.
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <L label="IMEI / Serial"><input value={(targetDevice || form).imei || ""} onChange={setTargetField("imei")} className={fld + " font-mono"} data-testid="edit-imei" /></L>
              <L label="Passcode"><input value={(targetDevice || form).passcode || ""} onChange={setTargetField("passcode")} className={fld + " font-mono"} data-testid="edit-passcode" /></L>
            </div>
          </section>

          {/* Repairs block — structured line items */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="overline">REPAIRS</div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => { setWizardReplaceIdx(null); setWizardOpen(true); }}
                  className="h-8 px-3.5 text-[11px] uppercase tracking-wider font-bold bg-[#1E5FEB] hover:bg-[#1849C8] text-white rounded-full flex items-center gap-1.5 transition-colors shadow-sm"
                  data-testid="edit-ticket-open-wizard"
                >
                  <Plus size={11} weight="bold" /> Add New
                </button>
                <button
                  type="button"
                  onClick={() => setShowCustomInput(true)}
                  className="h-8 px-3.5 text-[11px] uppercase tracking-wider font-bold border border-[#E5E7EB] hover:border-[#1E5FEB] hover:text-[#1E5FEB] rounded-full flex items-center gap-1.5 transition-colors"
                  data-testid="add-custom-repair"
                >
                  <Plus size={11} weight="bold" /> Custom
                </button>
              </div>
            </div>

            {/* Repair line items */}
            {repairLines.length > 0 ? (
              <div className="border border-[#E5E7EB] rounded-lg overflow-hidden" data-testid="repair-lines-table">
                {/* Header */}
                <div className="grid grid-cols-[1fr_80px_90px_32px_32px] gap-2 px-3 py-2 bg-[#F8FAFC] text-[9px] uppercase tracking-[0.14em] text-[#9CA3AF] font-semibold border-b border-[#E5E7EB]">
                  <div>Repair</div>
                  <div className="text-right">Catalog</div>
                  <div className="text-right">Price</div>
                  <div />
                  <div />
                </div>
                {/* Lines */}
                {repairLines.map((rl, idx) => (
                  <div
                    key={idx}
                    className="grid grid-cols-[1fr_80px_90px_32px_32px] gap-2 px-3 py-2.5 items-center border-b border-[#F3F4F6] last:border-0 hover:bg-[#FAFBFC] transition-colors"
                    data-testid={`repair-line-${idx}`}
                  >
                    <div className="min-w-0">
                      <div className="text-[13px] text-[#121216] truncate" title={rl.name}>{rl.name}</div>
                      <div className="mt-0.5 text-[10px] font-semibold text-[#64748B] truncate" data-testid={`repair-line-device-${idx}`}>
                        {lineDeviceLabel(rl)}
                      </div>
                    </div>
                    <div className="text-[12px] font-mono text-[#9CA3AF] text-right">
                      {rl.catalog_price != null ? `$${money(rl.catalog_price)}` : "—"}
                    </div>
                    <div>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={rl.price}
                        onChange={(e) => updateLinePrice(idx, e.target.value)}
                        className="money-field w-full h-8 px-2 text-[13px] font-mono text-right rounded border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none"
                        data-testid={`repair-line-price-${idx}`}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => { setWizardReplaceIdx(idx); setWizardOpen(true); }}
                      className="w-7 h-7 flex items-center justify-center rounded hover:bg-blue-50 text-[#9CA3AF] hover:text-[#1E5FEB] transition-colors"
                      data-testid={`repair-line-swap-${idx}`}
                      title="Swap this repair via wizard"
                    >
                      <PencilSimple size={14} weight="bold" />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeLine(idx)}
                      className="w-7 h-7 flex items-center justify-center rounded hover:bg-red-50 text-[#9CA3AF] hover:text-[#DC2626] transition-colors"
                      data-testid={`repair-line-delete-${idx}`}
                    >
                      <X size={14} weight="bold" />
                    </button>
                  </div>
                ))}
                {/* Total row */}
                <div className="grid grid-cols-[1fr_80px_90px_32px_32px] gap-2 px-3 py-2.5 bg-[#F8FAFC] border-t border-[#E5E7EB] items-center">
                  <div className="text-[12px] font-semibold text-[#121216]">Estimated Total</div>
                  <div />
                  <div className="text-[14px] font-mono font-bold text-[#1E5FEB] text-right" data-testid="repair-lines-total">
                    ${money(lineTotal)}
                  </div>
                  <div />
                  <div />
                </div>
              </div>
            ) : (
              <div className="border border-dashed border-[#E5E7EB] rounded-lg px-4 py-6 text-center">
                <div className="text-[13px] text-[#9CA3AF]">No repairs added yet</div>
                <div className="text-[11px] text-[#9CA3AF] mt-1">
                  Tap <span className="font-semibold text-[#121216]">Wizard</span> to walk through Brand → Device → Model → Issue, or add a custom line.
                </div>
              </div>
            )}

            {/* Custom repair input */}
            {showCustomInput && (
              <div className="mt-2 flex items-center gap-2 bg-[#F8FAFC] border border-[#E5E7EB] rounded-lg p-2" data-testid="custom-repair-input">
                <input
                  autoFocus
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  placeholder="Repair name…"
                  className="flex-1 h-8 px-2 text-[13px] rounded border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none min-w-0"
                  data-testid="custom-repair-name"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomRepair(); } }}
                />
                <input
                  value={customPrice}
                  onChange={(e) => setCustomPrice(e.target.value)}
                  placeholder="Price"
                  type="number"
                  min="0"
                  step="0.01"
                  className="money-field w-20 h-8 px-2 text-[13px] font-mono text-right rounded border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none"
                  data-testid="custom-repair-price"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomRepair(); } }}
                />
                <button
                  type="button"
                  onClick={addCustomRepair}
                  disabled={!customName.trim()}
                  className="h-8 px-3 text-[11px] font-semibold bg-[#1E5FEB] hover:bg-[#1849C8] disabled:bg-[#E5E7EB] disabled:text-[#9CA3AF] text-white rounded-lg transition-colors"
                  data-testid="custom-repair-add-btn"
                >
                  Add
                </button>
                <button
                  type="button"
                  onClick={() => { setShowCustomInput(false); setCustomName(""); setCustomPrice(""); }}
                  className="h-8 w-8 flex items-center justify-center rounded hover:bg-white text-[#9CA3AF] hover:text-[#121216] transition-colors"
                >
                  <X size={14} weight="bold" />
                </button>
              </div>
            )}

          </section>

          {/* ── Accessories ── */}
          <section data-testid="edit-accessories">
            <div className="flex items-center justify-between mb-2">
              <div className="overline">
                ACCESSORIES
                {accessories.length > 0 && (
                  <span className="ml-2 font-mono text-[#1E5FEB]" data-testid="edit-accessories-total">
                    ${accTotal.toFixed(2)}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={addCustomAcc}
                className="h-7 px-3 text-[10px] uppercase tracking-wider font-bold border border-[#E5E7EB] hover:border-[#1E5FEB] hover:text-[#1E5FEB] rounded-full flex items-center gap-1 transition-colors"
                data-testid="add-custom-accessory-btn"
              >
                <Plus size={10} weight="bold" /> Custom
              </button>
            </div>

            <div className="relative mb-2">
              <input
                value={accSearch}
                onChange={(e) => { setAccSearch(e.target.value); setAccOpen(true); }}
                onFocus={() => setAccOpen(true)}
                onBlur={() => setTimeout(() => setAccOpen(false), 150)}
                placeholder="Search to add · case, charger, screen protector…"
                className={fld}
                autoComplete="off"
                data-testid="edit-accessory-search"
              />
              {accOpen && accSearch.trim() && accResults.length > 0 && (
                <div
                  className="absolute left-0 right-0 top-full mt-1 bg-white rounded-lg border border-[#E5E7EB] overflow-hidden z-30 max-h-56 overflow-y-auto shadow-lg"
                  data-testid="edit-accessory-results"
                >
                  {accResults.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); addAcc(p); }}
                      className="w-full text-left px-3 py-2.5 flex items-center gap-2 border-t border-[#F3F4F6] first:border-t-0 hover:bg-[#F8FAFC] transition-colors"
                      data-testid={`edit-accessory-opt-${p.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-semibold text-[#121216] truncate">{p.name}</div>
                        <div className="text-[10px] font-mono text-[#9CA3AF] truncate">
                          {p.sku} · stock {p.stock ?? 0}
                        </div>
                      </div>
                      <div className="text-[13px] font-mono font-bold text-[#1E5FEB] shrink-0">
                        ${Number(p.price || 0).toFixed(2)}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {accessories.length === 0 ? (
              <div className="border border-dashed border-[#E5E7EB] rounded-lg px-4 py-4 text-center text-[12px] text-[#9CA3AF]">
                No accessories on this ticket — search above to add one
              </div>
            ) : (
              <div className="space-y-1.5" data-testid="edit-accessories-list">
                {accessories.map((a) => (
                  <div
                    key={a._key}
                    className="flex items-center gap-2 border border-[#E5E7EB] rounded-lg px-2.5 py-2"
                    data-testid={`edit-accessory-row-${a._key}`}
                  >
                    <div className="min-w-0 flex-1">
                      <input
                        value={a.name}
                        onChange={(e) => patchAcc(a._key, { name: e.target.value })}
                        placeholder="Accessory name"
                        className="w-full text-[13px] font-semibold text-[#121216] bg-transparent focus:outline-none"
                        data-testid={`edit-accessory-name-${a._key}`}
                      />
                      {a.sku && <div className="text-[10px] font-mono text-[#9CA3AF] truncate">{a.sku}</div>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => patchAcc(a._key, { quantity: Math.max(1, Number(a.quantity) - 1) })}
                        className="w-7 h-7 rounded border border-[#E5E7EB] hover:border-[#1E5FEB] hover:text-[#1E5FEB] font-bold transition-colors"
                        data-testid={`edit-accessory-dec-${a._key}`}
                      >−</button>
                      <div className="w-7 text-center text-[13px] font-mono font-bold tabular-nums">{a.quantity}</div>
                      <button
                        type="button"
                        onClick={() => patchAcc(a._key, { quantity: Number(a.quantity) + 1 })}
                        className="w-7 h-7 rounded border border-[#E5E7EB] hover:border-[#1E5FEB] hover:text-[#1E5FEB] font-bold transition-colors"
                        data-testid={`edit-accessory-inc-${a._key}`}
                      >+</button>
                    </div>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={a.unit_price}
                      onChange={(e) => patchAcc(a._key, { unit_price: e.target.value })}
                      className="money-field w-20 h-8 px-2 text-right text-[13px] font-mono font-bold tabular-nums rounded border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none shrink-0"
                      data-testid={`edit-accessory-price-${a._key}`}
                    />
                    <button
                      type="button"
                      onClick={() => removeAcc(a._key)}
                      className="w-7 h-7 flex items-center justify-center rounded hover:bg-red-50 text-[#9CA3AF] hover:text-[#DC2626] transition-colors shrink-0"
                      data-testid={`edit-accessory-remove-${a._key}`}
                    >
                      <X size={13} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="text-[9px] text-[#9CA3AF] mt-1.5">
              Accessories are billed on top of the repair estimate — they appear on the
              invoice and auto-load into the POS cart at pickup.
            </div>
          </section>

          {/* Pricing block */}
          <section>
            <div className="overline mb-2">PRICING</div>
            <div className="grid grid-cols-3 gap-3">
              <L label="Estimated ($)">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.estimated_cost}
                  onChange={setField("estimated_cost")}
                  className={fld + " money-field font-mono text-right" + (repairLines.length > 0 ? " bg-[#F8FAFC] text-[#9CA3AF]" : "")}
                  readOnly={repairLines.length > 0}
                  data-testid="edit-estimate"
                />
                {repairLines.length > 0 && (
                  <div className="text-[9px] text-[#9CA3AF] mt-1">Auto-calculated from repairs</div>
                )}
              </L>
              <L label="Final ($)">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.final_cost}
                  onChange={setField("final_cost")}
                  className={fld + " money-field font-mono text-right" + (repairLines.length > 0 ? " bg-[#F8FAFC] text-[#9CA3AF]" : "")}
                  readOnly={repairLines.length > 0}
                  data-testid="edit-final"
                />
                {repairLines.length > 0 && (
                  <div className="text-[9px] text-[#9CA3AF] mt-1">Synced with estimate</div>
                )}
              </L>
              <L label="Warranty (months)"><input type="number" min="0" step="1" value={form.warranty_months} onChange={setField("warranty_months")} className={fld + " font-mono text-right"} data-testid="edit-warranty" /></L>
            </div>
          </section>
        </div>

        <div className="flex gap-2 p-4 border-t border-[#E5E7EB] bg-[#FAFBFC] rounded-b-2xl shrink-0">
          <button type="button" onClick={onClose} className="flex-1 h-11 rounded-xl border border-[#E5E7EB] bg-white hover:bg-slate-50 text-[13px] font-semibold text-slate-700 transition-colors" data-testid="edit-ticket-cancel">Cancel</button>
          <button type="submit" disabled={saving} className="flex-1 h-11 rounded-xl bg-[#1E5FEB] hover:bg-[#1849C8] disabled:bg-zinc-300 text-white text-sm font-semibold" data-testid="edit-ticket-save">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
      <CatalogItemPicker
        open={wizardOpen}
        onClose={() => { setWizardOpen(false); setWizardReplaceIdx(null); }}
        onSelect={handleWizardSelect}
      />
    </div>
  );
}

function L({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.14em] text-[#9CA3AF] font-semibold mb-1">{label}</div>
      {children}
    </label>
  );
}

const CARD = "bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.05)]";

function SectionLabel({ icon: Icon, children }) {
  return (
    <div className="mb-3 flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.18em] font-bold text-slate-500">
      {Icon && <Icon size={14} weight="bold" />}{children}
    </div>
  );
}

function Info({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-1">{label}</div>
      <div className={`text-[14px] font-medium text-slate-900 ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}



/**
 * Modal to create and send an online invoice from a repair ticket.
 * Pre-fills line items from the ticket's repair_lines or issue text,
 * lets staff adjust, pick delivery channel (SMS/Email/Both), and send.
 */
function SendInvoiceModal({ open, ticket, linkedSales, onClose }) {
  const [items, setItems] = useState([]);
  const [note, setNote] = useState("");
  const [dueDays, setDueDays] = useState(7);
  const [sendVia, setSendVia] = useState("both");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null); // the created invoice
  const [catalogPickerOpen, setCatalogPickerOpen] = useState(false);

  useEffect(() => {
    if (!ticket || !open) return;
    setSent(null);
    setNote(`Repair for ${ticket.device_make || ""} ${ticket.device_model || ""}`.trim());

    // If there's already a linked sale, use its ACTUAL billed items + balance
    // as the source of truth — not ticket repair_lines which may differ.
    const activeSale = (linkedSales?.sales || []).find((s) => !s.voided);
    if (activeSale && activeSale.items?.length > 0) {
      const saleItems = (activeSale.items || []).map((it) => ({
        description: it.name || it.description || "Item",
        quantity: it.quantity || 1,
        unit_price: Number(it.unit_price || it.line_total || 0),
      }));

      // Add credit for what's already been paid in-store
      const alreadyPaid = Number(activeSale.amount_tendered || 0);
      if (alreadyPaid > 0.01) {
        saleItems.push({
          description: "Less: payments already received (in-store)",
          quantity: 1,
          unit_price: -Math.round(alreadyPaid * 100) / 100,
        });
      }
      setItems(saleItems);
      return;
    }

    // No linked sale — build line items from ticket repair data
    const repairItems = [];
    if (ticket.repair_lines?.length > 0) {
      ticket.repair_lines.forEach((rl) => {
        repairItems.push({ description: rl.name, quantity: 1, unit_price: rl.price || 0 });
      });
    } else if (ticket.issue) {
      // Parse prices from bullet text like "• Front Glass Repair ($119.00)"
      const parsed = parseIssueToRepairs(ticket.issue);
      if (parsed.length > 0) {
        parsed.forEach((r) => repairItems.push({ description: r.name, quantity: 1, unit_price: r.price || 0 }));
      } else {
        repairItems.push({ description: "Repair service", quantity: 1, unit_price: ticket.estimated_cost || 0 });
      }
    } else {
      repairItems.push({ description: "Repair service", quantity: 1, unit_price: ticket.estimated_cost || 0 });
    }

    // Include accessories from the ticket
    const ticketAccessories = ticket.pending_accessories || ticket.accessories || [];
    if (ticketAccessories.length > 0) {
      ticketAccessories.forEach((acc) => {
        repairItems.push({
          description: acc.name || acc.sku || "Accessory",
          quantity: acc.quantity || 1,
          unit_price: Number(acc.unit_price || 0),
        });
      });
    }

    // Include additional repair lines from additional devices
    if (ticket.additional_devices?.length > 0) {
      ticket.additional_devices.forEach((dev) => {
        const devLabel = `${dev.device_make || ""} ${dev.device_model || ""}`.trim();
        if (dev.repair_lines?.length > 0) {
          dev.repair_lines.forEach((rl) => {
            repairItems.push({ description: `${rl.name}${devLabel ? ` (${devLabel})` : ""}`, quantity: 1, unit_price: rl.price || 0 });
          });
        } else if (dev.issue) {
          // Parse prices from bullet text
          const parsed = parseIssueToRepairs(dev.issue);
          if (parsed.length > 0) {
            parsed.forEach((r) => repairItems.push({ description: `${r.name}${devLabel ? ` (${devLabel})` : ""}`, quantity: 1, unit_price: r.price || 0 }));
          } else if (dev.estimated_cost) {
            repairItems.push({ description: `Repair — ${devLabel || "Additional device"}`, quantity: 1, unit_price: dev.estimated_cost || 0 });
          }
        } else if (dev.estimated_cost) {
          repairItems.push({ description: `Repair — ${devLabel || "Additional device"}`, quantity: 1, unit_price: dev.estimated_cost || 0 });
        }
      });
    }

    setItems(repairItems);
  }, [ticket, linkedSales, open]);

  if (!open || !ticket) return null;

  const total = items.reduce((s, it) => s + it.quantity * it.unit_price, 0);

  const updateItem = (idx, field, val) => {
    setItems((prev) => prev.map((it, i) => i === idx ? { ...it, [field]: field === "description" ? val : Number(val) || 0 } : it));
  };
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const addItem = () => setItems((prev) => [...prev, { description: "", quantity: 1, unit_price: 0 }]);
  const addCatalogItem = (item) => {
    setItems((prev) => [...prev, { description: item.description, quantity: item.quantity, unit_price: item.unit_price }]);
    setCatalogPickerOpen(false);
  };

  const handleSend = async () => {
    if (items.length === 0) return toast.error("Add at least one line item");
    if (items.some((it) => !it.description.trim())) return toast.error("All items need a description");
    setSending(true);
    try {
      const { data } = await api.post("/online-invoices", {
        customer_id: ticket.customer_id,
        customer_name: ticket.customer_name,
        customer_email: ticket.customer_email || "",
        customer_phone: ticket.customer_phone || "",
        items,
        note,
        due_days: dueDays,
        linked_ticket_id: ticket.id,
        send_via: sendVia,
        device_make: ticket.device_make || "",
        device_type: ticket.device_type || "",
        device_model: ticket.device_model || "",
      });
      setSent(data);
      if (data.already_exists) {
        toast.info(`This ticket already has invoice ${data.invoice_number} — showing the existing one (no duplicate created)`, { duration: 6000 });
      } else {
        toast.success(`Invoice ${data.invoice_number} created and sent!`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const payLink = sent ? `${window.location.origin}/pay/${sent.payment_token}` : "";

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/55 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-lg max-h-[90vh] flex flex-col border border-[#E5E7EB] rounded-2xl" data-testid="send-invoice-modal">
        <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between shrink-0">
          <div>
            <div className="overline mb-1">SEND INVOICE</div>
            <h2 className="font-display font-semibold text-[20px] tracking-tight">
              {sent ? `Sent · ${sent.invoice_number}` : `Invoice for ${ticket.ticket_number}`}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-[#F8FAFC] rounded-full"><X size={18} weight="bold" /></button>
        </div>

        {sent ? (
          /* Success state */
          <div className="p-6 space-y-5 overflow-y-auto">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3">
              <PaperPlaneTilt size={24} weight="fill" className="text-emerald-500 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <div className="font-semibold text-[14px] text-emerald-800">Invoice Sent!</div>
                {(sent.sent_via === "sms" || sent.sent_via === "both") && ticket.customer_phone && (
                  <div className="text-[12px] text-emerald-700">
                    SMS sent to <span className="font-mono font-semibold">{ticket.customer_phone}</span>
                  </div>
                )}
                {(sent.sent_via === "email" || sent.sent_via === "both") && ticket.customer_email && (
                  <div className="text-[12px] text-emerald-700">
                    Email sent to <span className="font-semibold">{ticket.customer_email}</span>
                  </div>
                )}
                {sent.sent_via === "both" && !ticket.customer_phone && !ticket.customer_email && (
                  <div className="text-[12px] text-emerald-600">Invoice created — share the payment link manually</div>
                )}
              </div>
            </div>

            <div>
              <div className="overline mb-2">PAYMENT LINK</div>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={payLink}
                  className="flex-1 h-10 px-3 text-[12px] font-mono bg-[#F8FAFC] border border-[#E5E7EB] rounded-lg"
                  data-testid="invoice-pay-link"
                  onFocus={(e) => e.target.select()}
                />
                <button
                  onClick={() => { navigator.clipboard.writeText(payLink); toast.success("Link copied!"); }}
                  className="h-10 px-4 text-[12px] font-semibold border border-[#E5E7EB] hover:border-[#1E5FEB] hover:text-[#1E5FEB] rounded-lg transition-colors"
                  data-testid="copy-pay-link"
                >
                  COPY
                </button>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="p-3 bg-[#F8FAFC] rounded-lg">
                <div className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-semibold">Invoice</div>
                <div className="text-[14px] font-semibold text-[#121216] mt-1">{sent.invoice_number}</div>
              </div>
              <div className="p-3 bg-[#F8FAFC] rounded-lg">
                <div className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-semibold">Total</div>
                <div className="text-[14px] font-semibold text-[#121216] mt-1">${money(sent.total)}</div>
              </div>
              <div className="p-3 bg-[#F8FAFC] rounded-lg">
                <div className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-semibold">Status</div>
                <div className="text-[14px] font-semibold text-[#1E5FEB] mt-1 capitalize">{sent.status}</div>
              </div>
            </div>
          </div>
        ) : (
          /* Edit / Send state */
          <div className="p-6 space-y-5 overflow-y-auto">
            {/* Customer info */}
            <div className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-lg p-3">
              <div className="font-semibold text-[14px] text-[#121216]">{ticket.customer_name}</div>
              <div className="flex items-center gap-3 text-[12px] text-[#4B5563] mt-0.5 flex-wrap">
                {ticket.customer_phone && <span className="font-mono">{ticket.customer_phone}</span>}
                {ticket.customer_email && <span>{ticket.customer_email}</span>}
              </div>
              {!ticket.customer_phone && !ticket.customer_email && (
                <div className="text-[11px] text-amber-600 mt-1">No phone or email on file — add to customer profile first, or copy the payment link manually.</div>
              )}
            </div>

            {/* Line items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="overline">LINE ITEMS</div>
                <div className="flex items-center gap-3">
                  <button type="button" onClick={() => setCatalogPickerOpen(true)} className="h-7 px-3 text-[10px] uppercase tracking-wider font-bold border border-[#1E5FEB] text-[#1E5FEB] hover:bg-[#1E5FEB] hover:text-white rounded-full flex items-center gap-1 transition-colors" data-testid="invoice-add-from-catalog">
                    <Plus size={10} weight="bold" /> FROM CATALOG
                  </button>
                  <button type="button" onClick={addItem} className="h-7 px-3 text-[10px] uppercase tracking-wider font-bold border border-[#E5E7EB] hover:border-[#1E5FEB] hover:text-[#1E5FEB] rounded-full flex items-center gap-1" data-testid="invoice-add-item">
                    <Plus size={10} weight="bold" /> ADD
                  </button>
                </div>
              </div>
              <div className="border border-[#E5E7EB] rounded-lg overflow-hidden divide-y divide-[#F3F4F6]" data-testid="invoice-items">
                {items.map((it, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-2" data-testid={`invoice-item-${i}`}>
                    <input
                      value={it.description}
                      onChange={(e) => updateItem(i, "description", e.target.value)}
                      placeholder="Description"
                      className="flex-1 h-8 px-2 text-[13px] rounded border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none min-w-0"
                    />
                    <input
                      type="number"
                      min="1"
                      value={it.quantity}
                      onChange={(e) => updateItem(i, "quantity", e.target.value)}
                      className="w-14 h-8 px-2 text-[13px] font-mono text-center rounded border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none"
                      title="Qty"
                    />
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={it.unit_price}
                      onChange={(e) => updateItem(i, "unit_price", e.target.value)}
                      className="money-field w-20 h-8 px-2 text-[13px] font-mono text-right rounded border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none"
                      title="Unit price"
                    />
                    <button type="button" onClick={() => removeItem(i)} className="w-7 h-7 flex items-center justify-center rounded hover:bg-red-50 text-[#9CA3AF] hover:text-[#DC2626]">
                      <X size={14} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex justify-end mt-2 text-[16px] font-mono font-bold text-[#121216]" data-testid="invoice-total">
                Total: ${total.toFixed(2)}
              </div>
            </div>

            {/* Note */}
            <div>
              <div className="overline mb-1">NOTE (optional)</div>
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="w-full h-10 px-3 text-[13px] rounded-lg border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none"
                placeholder="e.g. Repair for iPhone 14 Pro"
                data-testid="invoice-note"
              />
            </div>

            {/* Due date + Send via */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="overline mb-1">DUE IN</div>
                <select
                  value={dueDays}
                  onChange={(e) => setDueDays(Number(e.target.value))}
                  className="w-full h-10 px-3 text-[13px] rounded-lg border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none bg-white"
                  data-testid="invoice-due-days"
                >
                  <option value={3}>3 days</option>
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                </select>
              </div>
              <div>
                <div className="overline mb-1">SEND VIA</div>
                <select
                  value={sendVia}
                  onChange={(e) => setSendVia(e.target.value)}
                  className="w-full h-10 px-3 text-[13px] rounded-lg border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none bg-white"
                  data-testid="invoice-send-via"
                >
                  <option value="both">SMS + Email</option>
                  <option value="sms">SMS only</option>
                  <option value="email">Email only</option>
                </select>
              </div>
            </div>
            {/* Sending destination preview */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5 space-y-1" data-testid="send-destination">
              <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-blue-700">Sending to</div>
              {(sendVia === "sms" || sendVia === "both") && (
                <div className="text-[12px] text-blue-800 flex items-center gap-1.5">
                  <span className="font-semibold">SMS:</span>
                  <span className="font-mono">{ticket.customer_phone || "— no phone on file"}</span>
                </div>
              )}
              {(sendVia === "email" || sendVia === "both") && (
                <div className="text-[12px] text-blue-800 flex items-center gap-1.5">
                  <span className="font-semibold">Email:</span>
                  <span>{ticket.customer_email || "— no email on file"}</span>
                </div>
              )}
              {((sendVia === "sms" || sendVia === "both") && !ticket.customer_phone) && (
                <div className="text-[11px] text-amber-700 font-semibold mt-0.5">SMS won't be sent — no phone number</div>
              )}
              {((sendVia === "email" || sendVia === "both") && !ticket.customer_email) && (
                <div className="text-[11px] text-amber-700 font-semibold mt-0.5">Email won't be sent — no email address</div>
              )}
            </div>
          </div>
        )}

        <div className="flex gap-2 p-4 border-t border-[#E5E7EB] bg-[#FAFBFC] rounded-b-2xl shrink-0">
          {sent ? (
            <button onClick={onClose} className="flex-1 h-11 rounded-xl border border-[#E5E7EB] bg-white hover:bg-slate-50 text-[13px] font-semibold text-slate-700 transition-colors" data-testid="invoice-done-btn">Done</button>
          ) : (
            <>
              <button type="button" onClick={onClose} className="flex-1 h-11 rounded-xl border border-[#E5E7EB] bg-white hover:bg-slate-50 text-[13px] font-semibold text-slate-700 transition-colors" data-testid="invoice-cancel-btn">Cancel</button>
              <button
                onClick={handleSend}
                disabled={sending || items.length === 0}
                className="flex-1 h-11 rounded-xl bg-[#1E5FEB] hover:bg-[#1849C8] disabled:bg-zinc-300 text-white text-sm font-semibold flex items-center justify-center gap-2"
                data-testid="invoice-send-btn"
              >
                {sending ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Sending…</>
                ) : (
                  <><PaperPlaneTilt size={16} weight="bold" /> Send ${total.toFixed(2)} invoice</>
                )}
              </button>
            </>
          )}
        </div>
      </div>
      <CatalogItemPicker
        open={catalogPickerOpen}
        onClose={() => setCatalogPickerOpen(false)}
        onSelect={addCatalogItem}
      />
    </div>
  );
}
